import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";

import {
  getInterestLeads,
  getListings,
  getListing,
  getListingViewings,
  createListing,
  getOffers,
  createOffer,
  updateOffer,
  updateOfferStatus,
  deleteOffer,
  getContacts,
  createContact
} from "../api/client.js";
export { CrmContactsPage } from "./CrmContactsPage.jsx";
export { default as CrmContactCreatePage } from "./CrmContactCreatePage.jsx";
export { default as CrmPipelinePage } from "./CrmPipelinePage.jsx";
export { default as CrmDeletedContactsPage } from "./CrmDeletedContactsPage.jsx";
export { default as CrmAmlReviewPage } from "./CrmAmlReviewPage.jsx";
export { default as CrmAuditLogPage } from "./CrmAuditLogPage.jsx";
export { default as CrmAiLogsPage } from "./CrmAiLogsPage.jsx";
export { default as CrmGdprPage } from "./CrmGdprPage.jsx";
export { default as CrmPaymentHistoryPage } from "./CrmPaymentHistoryPage.jsx";
import { LISTINGS } from "../data/listings";
import { Roles, clearSession } from "../auth/session.js";

const NAV_ITEMS = [
  { to: "/crm/dashboard", label: "Dashboard" },
  { to: "/crm/listings", label: "Listings" },
  { to: "/crm/contacts", label: "Contacts" },
  { to: "/crm/pipeline", label: "Pipeline" },
  { to: "/crm/viewings", label: "Viewings" },
  { to: "/crm/offers", label: "Offers" },
  { to: "/crm/sales", label: "Sales" },
  { to: "/crm/letters", label: "Letters" },
  { to: "/crm/payments", label: "Payments" },
  { to: "/crm/aml", label: "AML Review", adminOnly: true },
  { to: "/crm/audit-log", label: "Audit Log", adminOnly: true },
  { to: "/crm/ai-logs", label: "AI Logs", adminOnly: true },
  { to: "/crm/gdpr", label: "GDPR Tools" },
  { to: "/crm/settings", label: "Settings" },
  { to: "/crm/admin/deleted-contacts", label: "Deleted contacts", adminOnly: true }
];

// Icon components for each navigation item
function NavItemIcon({ label }) {
  const iconProps = { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "w-5 h-5" };
  
  switch(label) {
    case "Dashboard":
      return (
        <svg {...iconProps}>
          <path d="M3 4H9V10H3V4Z" fill="currentColor" opacity="0.8"/>
          <path d="M11 4H17V10H11V4Z" fill="currentColor" opacity="0.6"/>
          <path d="M3 12H9V18H3V12Z" fill="currentColor" opacity="0.6"/>
          <path d="M11 12H17V18H11V12Z" fill="currentColor"/>
        </svg>
      );
    case "Listings":
      return (
        <svg {...iconProps}>
          <path d="M3 3H17V7H3V3Z" fill="currentColor"/>
          <path d="M3 9H17V13H3V9Z" fill="currentColor" opacity="0.6"/>
          <path d="M3 15H17V19H3V15Z" fill="currentColor" opacity="0.4"/>
        </svg>
      );
    case "Contacts":
      return (
        <svg {...iconProps}>
          <circle cx="10" cy="6" r="3.5" fill="currentColor"/>
          <path d="M2 18C2 13.58 5.58 10 10 10C14.42 10 18 13.58 18 18V19H2V18Z" fill="currentColor" opacity="0.8"/>
        </svg>
      );
    case "Pipeline":
      return (
        <svg {...iconProps}>
          <path d="M2 16L6 12L10 14L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <circle cx="6" cy="12" r="1.5" fill="currentColor"/>
          <circle cx="10" cy="14" r="1.5" fill="currentColor"/>
        </svg>
      );
    case "Viewings":
      return (
        <svg {...iconProps}>
          <path d="M4 5H16V7H4V5Z" fill="currentColor"/>
          <path d="M4 9H16V17H4V9Z" fill="currentColor" opacity="0.8"/>
          <path d="M7 12H9V14H7V12Z" fill="currentColor" opacity="0.5"/>
          <path d="M11 12H13V14H11V12Z" fill="currentColor" opacity="0.5"/>
        </svg>
      );
    case "Offers":
      return (
        <svg {...iconProps}>
          <path d="M3 4H17V7H3V4Z" fill="currentColor" opacity="0.6"/>
          <path d="M3 8H8V18H3V8Z" fill="currentColor"/>
          <path d="M9 8H14V18H9V8Z" fill="currentColor" opacity="0.8"/>
          <path d="M15 8H17V18H15V8Z" fill="currentColor" opacity="0.6"/>
        </svg>
      );
    case "Sales":
      return (
        <svg {...iconProps}>
          <path d="M10 2L14 8H6L10 2Z" fill="currentColor"/>
          <path d="M6 9H14L12 16H8L6 9Z" fill="currentColor" opacity="0.8"/>
          <circle cx="10" cy="13" r="1" fill="currentColor" opacity="0.5"/>
        </svg>
      );
    case "Letters":
      return (
        <svg {...iconProps}>
          <rect x="2" y="3" width="16" height="14" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 3L10 10L18 3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
        </svg>
      );
    case "Payments":
      return (
        <svg {...iconProps}>
          <rect x="2" y="4" width="16" height="12" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 7H18" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="15" cy="12" r="1.5" fill="currentColor"/>
        </svg>
      );
    case "AML Review":
      return (
        <svg {...iconProps}>
          <path d="M10 2L12 6H16L13 8L14 12L10 9L6 12L7 8L4 6H8L10 2Z" fill="currentColor"/>
        </svg>
      );
    case "Audit Log":
      return (
        <svg {...iconProps}>
          <path d="M4 3H16V5H4V3Z" fill="currentColor" opacity="0.6"/>
          <path d="M4 7H16V9H4V7Z" fill="currentColor" opacity="0.8"/>
          <path d="M4 11H16V13H4V11Z" fill="currentColor"/>
          <path d="M4 15H16V17H4V15Z" fill="currentColor" opacity="0.6"/>
        </svg>
      );
    case "AI Logs":
      return (
        <svg {...iconProps}>
          <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M10 6V10L13 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
        </svg>
      );
    case "GDPR Tools":
      return (
        <svg {...iconProps}>
          <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2Z" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M10 5V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="10" cy="15" r="0.8" fill="currentColor"/>
        </svg>
      );
    case "Settings":
      return (
        <svg {...iconProps}>
          <circle cx="10" cy="10" r="2.5" fill="currentColor"/>
          <path d="M10 2V5M10 15V18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M5.86 4.86L7.64 6.64M12.36 13.36L14.14 15.14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M2 10H5M15 10H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M4.86 14.14L6.64 12.36M13.36 7.64L15.14 5.86" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case "Deleted contacts":
      return (
        <svg {...iconProps}>
          <path d="M3 5H4H17V7H3V5Z" fill="currentColor"/>
          <path d="M5 8H15V16C15 17.1 14.1 18 13 18H7C5.9 18 5 17.1 5 16V8Z" fill="currentColor" opacity="0.8"/>
          <path d="M7 10H9V14H7V10Z" fill="currentColor" opacity="0.5"/>
          <path d="M11 10H13V14H11V10Z" fill="currentColor" opacity="0.5"/>
        </svg>
      );
    default:
      return (
        <svg {...iconProps}>
          <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M10 7V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
  }
}

const SEARCH_TYPE_STYLES = {
  "Board request": "border-amber-400/40 bg-amber-500/10 text-amber-100",
  Compliance: "border-rose-400/40 bg-rose-500/10 text-rose-100",
  Offer: "border-sky-400/40 bg-sky-500/10 text-sky-100",
  Viewing: "border-emerald-400/40 bg-emerald-500/10 text-emerald-100",
  Seller: "border-purple-400/40 bg-purple-500/10 text-purple-100",
  Listing: "border-white/20 bg-white/10 text-white/80"
};

const SELLER_PROFILES_KEY = "home_seller_profiles_v1";
const USER_PROFILES_KEY = "home_user_profiles_v1";
const CRM_PIPELINE_TARGETS = { clients: 12, onboarding: 3, active: 5 };

const CRM_STATUS_OPTIONS = [
  { label: "All listings", value: "all" },
  { label: "Coming soon", value: "COMING_SOON" },
  { label: "Active", value: "ACTIVE" },
  { label: "Under offer", value: "UNDER_OFFER" },
  { label: "Sold", value: "SOLD" },
  { label: "Withdrawn", value: "WITHDRAWN" }
];

const STATUS_LABELS = {
  COMING_SOON: "Coming soon",
  ACTIVE: "Active",
  UNDER_OFFER: "Under offer",
  SOLD: "Sold",
  WITHDRAWN: "Withdrawn"
};

const CRM_LISTINGS_PAGE_SIZE = 12;

const NEW_LISTING_FORM = {
  refCode: "",
  status: "ACTIVE",
  priceGuide: "",
  beds: "",
  baths: "",
  sqft: "",
  tenure: "",
  line1: "",
  line2: "",
  city: "",
  postcode: "",
  description: "",
  features: ""
};

function offsetDate(daysFromToday) {
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  base.setDate(base.getDate() + daysFromToday);
  return base.toISOString().slice(0, 10);
}

function hoursAgo(hours) {
  const base = new Date();
  base.setHours(base.getHours() - hours);
  return base;
}

function minutesAgo(minutes) {
  const base = new Date();
  base.setMinutes(base.getMinutes() - minutes);
  return base;
}

function combineDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const iso = `${dateStr}T${timeStr || "09:00"}`;
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatCurrency(amount, currency = "GBP") {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(amount || 0);
}

function formatShortDate(date) {
  if (!date) return "—";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short"
  });
}

function formatShortTime(date) {
  if (!date) return "—";
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatShortDateTime(date) {
  if (!date) return "—";
  return `${formatShortDate(date)} • ${formatShortTime(date)}`;
}

function formatRelative(date) {
  if (!date) return "";
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  const absMinutes = Math.round(absMs / 60000);
  if (absMinutes < 1) return diffMs >= 0 ? "in moments" : "just now";
  if (absMinutes < 60) {
    return diffMs >= 0 ? `in ${absMinutes}m` : `${absMinutes}m ago`;
  }
  const absHours = Math.round(absMs / 3600000);
  if (absHours < 48) {
    return diffMs >= 0 ? `in ${absHours}h` : `${absHours}h ago`;
  }
  const absDays = Math.round(absMs / 86400000);
  return diffMs >= 0 ? `in ${absDays}d` : `${absDays}d ago`;
}

function isWithinDaysFromNow(date, days) {
  if (!date) return false;
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  return diff >= 0 && diff <= days * 86400000;
}

// Buyer offer localStorage functions removed - use API exclusively

function deriveDisplayName(email, fallback = "Contact") {
  if (!email) return fallback;
  const [prefix] = email.split("@");
  if (!prefix) return fallback;
  return prefix
    .split(/[._-]/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(" ");
}

const BASE_LISTING_ID = LISTINGS[0]?.id || "w8-123";
const SECONDARY_LISTING_ID = LISTINGS[1]?.id || BASE_LISTING_ID;

const CRM_SELLER_BASELINE = {
  "seller@test.com": {
    contactName: "Seller Summit",
    status: "active",
    address: "123 Kensington High St, London W8",
    postcode: "W8 5TT",
    planKey: "premier",
    activeListingId: BASE_LISTING_ID,
    marketingLaunch: offsetDate(-5),
    boardPreference: "yes",
    boardInstallBooked: null,
    boardRequestUpdatedAt: hoursAgo(16).toISOString(),
    ownershipDocReceived: true,
    idDocsVerified: true,
    amlCleared: true,
    epcDocument: true,
    valuationRange: { lower: 1800000, upper: 1900000, midpoint: 1850000 },
    salesStage: "offers"
  },
  "valuation@test.com": {
    contactName: "Valuation Preview",
    status: "prospect",
    address: "45 Brookfield Avenue, London N1",
    postcode: "N1 3AB",
    planKey: "core",
    boardPreference: "undecided",
    boardInstallBooked: null,
    boardRequestUpdatedAt: hoursAgo(4).toISOString(),
    ownershipDocReceived: false,
    idDocsVerified: false,
    amlCleared: false,
    epcDocument: false,
    valuationRange: { lower: 820000, upper: 910000, midpoint: 865000 },
    salesStage: "valuation"
  },
  "developer@test.com": {
    contactName: "Developer Lead",
    status: "checkout",
    address: "18 Harbour Point, Canary Wharf E14",
    postcode: "E14 9SH",
    planKey: "premier",
    activeListingId: SECONDARY_LISTING_ID,
    marketingLaunch: offsetDate(1),
    boardPreference: "yes",
    boardInstallBooked: offsetDate(3),
    boardRequestUpdatedAt: hoursAgo(8).toISOString(),
    ownershipDocReceived: true,
    idDocsVerified: true,
    amlCleared: true,
    epcDocument: true,
    valuationRange: { lower: 1250000, upper: 1350000, midpoint: 1300000 },
    salesStage: "onboarding"
  }
};

const CRM_USER_BASELINE = {
  "seller@test.com": { displayName: "Seller Summit" },
  "valuation@test.com": { displayName: "Valuation Preview" },
  "developer@test.com": { displayName: "Developer Lead" },
  "buyer@test.com": { displayName: "Buyer Beta" },
  "buyerseller@test.com": { displayName: "Hybrid Hero" },
  "priya.dan@buyers.com": { displayName: "Priya & Dan" },
  "corporate@relocators.com": { displayName: "Corporate Relocation" },
  "luca@buyers.com": { displayName: "Luca P." },
  "agent@test.com": { displayName: "Agent Apex" },
  "lila@thebreakco.com": { displayName: "Lila @ HOME" }
};

const CRM_AGENT_CONTACTS = [
  {
    email: "lila@thebreakco.com",
    name: "Lila @ HOME",
    phone: "+44 20 1111 2222",
    company: "HOME AI",
    role: "Agent admin",
    notes: "Oversees onboarding and compliance."
  },
  {
    email: "mo@thebreakco.com",
    name: "Mo @ HOME",
    phone: "+44 20 3333 4444",
    company: "HOME AI",
    role: "Agent",
    notes: "Leads high-value marketing campaigns."
  },
  {
    email: "agent@test.com",
    name: "Agent Apex",
    phone: "+44 20 5555 9000",
    company: "HOME AI",
    role: "Agent",
    notes: "Assigned to Kensington listings."
  }
];

const CRM_LEGAL_CONTACTS = [
  {
    email: "alex.morgan@legalpartners.co.uk",
    name: "Alex Morgan",
    company: "Legal Partners LLP",
    phone: "+44 20 7000 1122",
    notes: "Preferred conveyancer; integrated with offer workflow."
  },
  {
    email: "sarah.cho@citylawgroup.com",
    name: "Sarah Cho",
    company: "City Law Group",
    phone: "+44 20 7555 8877",
    notes: "Handles new build developer contracts."
  }
];

const CRM_VIEWINGS_BASELINE = [
  {
    id: "vw-1",
    listingId: BASE_LISTING_ID,
    sellerEmail: "seller@test.com",
    buyerEmail: "priya.dan@buyers.com",
    buyerName: "Priya & Dan",
    agent: "Agent Apex",
    status: "Confirmed",
    date: offsetDate(1),
    time: "15:00",
    notes: "Confirm parking instructions 24h prior."
  },
  {
    id: "vw-2",
    listingId: BASE_LISTING_ID,
    sellerEmail: "seller@test.com",
    buyerEmail: "corporate@relocators.com",
    buyerName: "Corporate relocation",
    agent: "Lila @ HOME",
    status: "Awaiting confirm",
    date: offsetDate(3),
    time: "11:30",
    notes: "Need video tour if diary clashes."
  },
  {
    id: "vw-3",
    listingId: SECONDARY_LISTING_ID,
    sellerEmail: "developer@test.com",
    buyerEmail: "buyer@test.com",
    buyerName: "Buyer Beta",
    agent: "Agent Apex",
    status: "Scheduled",
    date: offsetDate(5),
    time: "10:00",
    notes: "Pre-launch walkthrough with developer contact."
  }
];

const CRM_OFFERS_BASELINE = [
  {
    id: "of-101",
    listingId: BASE_LISTING_ID,
    sellerEmail: "seller@test.com",
    buyerEmail: "priya.dan@buyers.com",
    buyerName: "Priya & Dan",
    amount: 1825000,
    status: "Awaiting proof",
    updatedAt: hoursAgo(4).toISOString(),
    notes: "Proof of funds requested — due COB today."
  },
  {
    id: "of-102",
    listingId: SECONDARY_LISTING_ID,
    sellerEmail: "developer@test.com",
    buyerEmail: "luca@buyers.com",
    buyerName: "Luca P.",
    amount: 1285000,
    status: "Counter sent",
    updatedAt: hoursAgo(19).toISOString(),
    notes: "Replied with revised completion timeline."
  }
];

const CRM_ACTIVITY_BASELINE = [
  {
    id: "act-ops",
    title: "Marketing performance summary sent to Seller Summit",
    detail: "Agent Apex recapped portal leads for the past 7 days.",
    timestamp: minutesAgo(50).toISOString(),
    tone: "info"
  },
  {
    id: "act-buyer",
    title: "New buyer registration: buyer@test.com",
    detail: "Preferences saved for SW3 / £1.2m. Assigned to Agent Apex.",
    timestamp: hoursAgo(9).toISOString(),
    tone: "neutral"
  }
];

const CONTACT_TYPE_ORDER = ["Buyer & Seller", "Seller", "Buyer", "Legal", "Agent", "Site member"];

function buildContactDirectory({ sellers, userProfiles, listingMap }) {
  const directory = new Map();

  const ensure = (email, defaults = {}) => {
    if (!email) return null;
    const key = email.toLowerCase();
    if (!directory.has(key)) {
      directory.set(key, {
        email,
        name: defaults.name || deriveDisplayName(email),
        phone: defaults.phone || "",
        company: defaults.company || "",
        notes: defaults.notes || "",
        types: new Set(defaults.type ? [defaults.type] : []),
        relatedListings: new Set(defaults.relatedListings || []),
        lastInteraction: defaults.lastInteraction ? new Date(defaults.lastInteraction) : null
      });
    } else if (defaults.name) {
      directory.get(key).name = defaults.name;
    }
    const record = directory.get(key);
    if (defaults.phone && !record.phone) record.phone = defaults.phone;
    if (defaults.company && !record.company) record.company = defaults.company;
    if (defaults.notes) {
      record.notes = record.notes ? `${record.notes} ${defaults.notes}`.trim() : defaults.notes;
    }
    if (defaults.type) record.types.add(defaults.type);
    if (defaults.relatedListings) {
      defaults.relatedListings.forEach((id) => record.relatedListings.add(id));
    }
    if (defaults.lastInteraction) {
      const dt = new Date(defaults.lastInteraction);
      if (!record.lastInteraction || dt > record.lastInteraction) {
        record.lastInteraction = dt;
      }
    }
    return record;
  };

  sellers.forEach((seller) => {
    ensure(seller.email, {
      name: seller.contactName || deriveDisplayName(seller.email),
      type: "Seller",
      phone: seller.phone,
      notes:
        seller.status === "checkout"
          ? "Onboarding"
          : seller.status === "active"
          ? "Active instruction"
          : "Prospect",
      relatedListings: seller.activeListingId ? [seller.activeListingId] : [],
      lastInteraction: seller._updatedAt || seller.boardRequestUpdatedAt || Date.now()
    });
  });

  CRM_AGENT_CONTACTS.forEach((agent) => {
    ensure(agent.email, {
      name: agent.name,
      type: "Agent",
      phone: agent.phone,
      company: agent.company,
      notes: agent.role ? `${agent.role}. ${agent.notes || ""}`.trim() : agent.notes || ""
    });
  });

  CRM_LEGAL_CONTACTS.forEach((contact) => {
    ensure(contact.email, {
      name: contact.name,
      company: contact.company,
      phone: contact.phone,
      type: "Legal",
      notes: contact.notes
    });
  });

  CRM_VIEWINGS_BASELINE.forEach((viewing) => {
    const occursAt = combineDateTime(viewing.date, viewing.time);
    ensure(viewing.buyerEmail, {
      name: viewing.buyerName,
      type: "Buyer",
      relatedListings: [viewing.listingId],
      lastInteraction: occursAt
    });
    ensure(viewing.sellerEmail, {
      type: "Seller",
      relatedListings: [viewing.listingId],
      lastInteraction: occursAt
    });
  });

  CRM_OFFERS_BASELINE.forEach((offer) => {
    ensure(offer.buyerEmail, {
      name: offer.buyerName,
      type: "Buyer",
      relatedListings: [offer.listingId],
      lastInteraction: offer.updatedAt
    });
    ensure(offer.sellerEmail, {
      type: "Seller",
      relatedListings: [offer.listingId],
      lastInteraction: offer.updatedAt
    });
  });

  Object.entries(userProfiles).forEach(([email, profile]) => {
    ensure(email, {
      name: profile.displayName,
      notes: profile.summary,
      type: "Site member"
    });
  });

  const contacts = Array.from(directory.values()).map((record) => {
    const types = Array.from(record.types);
    if (types.includes("Seller") && types.includes("Buyer")) {
      record.types = new Set(["Buyer & Seller"]);
    }
    const finalTypes = Array.from(record.types);
    const orderedType =
      finalTypes.sort(
        (a, b) => CONTACT_TYPE_ORDER.indexOf(a) - CONTACT_TYPE_ORDER.indexOf(b)
      )[0] || "Site member";
    return {
      ...record,
      type: orderedType,
      types: finalTypes,
      relatedListings: Array.from(record.relatedListings)
        .map((id) => listingMap[id]?.title || id)
        .filter(Boolean)
    };
  });

  return contacts.sort((a, b) => {
    const diff = CONTACT_TYPE_ORDER.indexOf(a.type) - CONTACT_TYPE_ORDER.indexOf(b.type);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name);
  });
}

function collectSellerRecords() {
  // Return baseline seller data only - localStorage removed
  const merged = {};
  Object.entries(CRM_SELLER_BASELINE).forEach(([email, record]) => {
    merged[email] = { ...record };
  });
  return Object.entries(merged).map(([email, record]) => ({
    email,
    ...record,
    status: (record.status || "prospect").toLowerCase()
  }));
}

function collectUserProfiles() {
  // Return baseline user profiles only - localStorage removed
  return { ...CRM_USER_BASELINE };
}

function buildDashboardData() {
  const sellers = collectSellerRecords();
  const userProfiles = collectUserProfiles();
  const listingMap = Object.fromEntries(LISTINGS.map((listing) => [listing.id, listing]));
  const contacts = buildContactDirectory({ sellers, userProfiles, listingMap });
  // Buyer offer submissions removed - use API exclusively

  const viewings = CRM_VIEWINGS_BASELINE.map((viewing) => {
    const occursAt = combineDateTime(viewing.date, viewing.time);
    const listing = listingMap[viewing.listingId] || null;
    const seller = sellers.find((candidate) => candidate.email === viewing.sellerEmail) || null;
    const buyerProfile = userProfiles[viewing.buyerEmail] || {};
    const buyerLabel = viewing.buyerName || buyerProfile.displayName || deriveDisplayName(viewing.buyerEmail);
    return {
      ...viewing,
      occursAt,
      listing,
      seller,
      buyerLabel,
      feedbackEntry: viewing.feedback || null // Use API feedback field
    };
  }).sort((a, b) => {
    const aTime = a.occursAt ? a.occursAt.getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.occursAt ? b.occursAt.getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

  const combinedOffers = CRM_OFFERS_BASELINE;

  const offers = combinedOffers
    .map((offer) => {
      const listing = listingMap[offer.listingId] || null;
      const seller =
        sellers.find((candidate) => candidate.email === offer.sellerEmail) || sellers[0] || null;
      const buyerProfile = userProfiles[offer.buyerEmail] || {};
      const buyerLabel = offer.buyerName || buyerProfile.displayName || deriveDisplayName(offer.buyerEmail);
      const updatedAt = offer.updatedAt ? new Date(offer.updatedAt) : offer.updated ? new Date(offer.updated) : null;
      return {
        ...offer,
        listing,
        seller,
        buyerLabel,
        amountDisplay: formatCurrency(offer.amount || listing?.price || 0),
        updatedAt,
        editable: Boolean(offer.source),
        documents: offer.documents || []
      };
    })
    .sort((a, b) => {
      const aTime = a.updatedAt ? a.updatedAt.getTime() : 0;
      const bTime = b.updatedAt ? b.updatedAt.getTime() : 0;
      return bTime - aTime;
    });

  const counts = sellers.reduce(
    (acc, seller) => {
      const status = seller.status || "prospect";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    { prospect: 0, checkout: 0, active: 0 }
  );

  const activeListingIds = new Set();
  sellers.forEach((seller) => {
    if (seller.activeListingId) activeListingIds.add(seller.activeListingId);
    if (Array.isArray(seller.properties)) {
      seller.properties.forEach((property) => {
        if (property.listingId) activeListingIds.add(property.listingId);
      });
    }
  });
  const activeListings = [...activeListingIds]
    .map((id) => listingMap[id])
    .filter(Boolean);

  const contactsByType = contacts.reduce((acc, contact) => {
    acc[contact.type] = (acc[contact.type] || 0) + 1;
    return acc;
  }, {});

  const viewingsThisWeek = viewings.filter((viewing) => isWithinDaysFromNow(viewing.occursAt, 7)).length;
  const pendingViewings = viewings.filter((viewing) =>
    (viewing.status || "").toLowerCase().includes("await")
  ).length;

  const offerPipeline = offers.filter((offer) => {
    const status = (offer.status || "").toLowerCase();
    return !status.includes("withdrawn") && !status.includes("completed");
  });
  const totalOfferVolume = offerPipeline.reduce((sum, offer) => sum + (offer.amount || 0), 0);
  const acceptedOffers = offers.filter((offer) => (offer.status || "").toLowerCase().includes("accept"));

  const viewingsByListing = viewings.reduce((acc, viewing) => {
    if (!viewing.listingId) return acc;
    acc[viewing.listingId] = (acc[viewing.listingId] || 0) + 1;
    return acc;
  }, {});
  const offersByListing = offers.reduce((acc, offer) => {
    if (!offer.listingId) return acc;
    const status = (offer.status || "").toLowerCase();
    if (!status.includes("withdrawn")) {
      acc[offer.listingId] = (acc[offer.listingId] || 0) + 1;
    }
    return acc;
  }, {});

  const metrics = [
    {
      key: "listings",
      label: "Active listings",
      value: activeListings.length,
      meta:
        acceptedOffers.length > 0
          ? `${acceptedOffers.length} under offer`
          : `${offerPipeline.length} offer${offerPipeline.length === 1 ? "" : "s"} in play`
    },
    {
      key: "clients",
      label: "Clients",
      value: contacts.length,
      meta: `${contactsByType["Seller"] || 0} sellers • ${contactsByType["Buyer"] || 0} buyers`
    },
    {
      key: "viewings",
      label: "Viewings (7d)",
      value: viewingsThisWeek,
      meta:
        pendingViewings > 0
          ? `${pendingViewings} awaiting confirm`
          : "All confirmed"
    },
    {
      key: "offers",
      label: "Offers in review",
      value: offerPipeline.length,
      meta: `${formatCurrency(totalOfferVolume)} pipeline`
    }
  ];

  const pipeline = [
    {
      key: "clients",
      title: "Clients",
      count: contacts.length,
      target: CRM_PIPELINE_TARGETS.clients,
      progress: Math.min(100, Math.round((contacts.length / CRM_PIPELINE_TARGETS.clients) * 100)),
      status:
        contacts.length === 0
          ? "empty"
          : contacts.length >= CRM_PIPELINE_TARGETS.clients
          ? "healthy"
          : "focus",
      description: "Active relationships across buyers, sellers, and partners.",
      highlights: contacts.slice(0, 3).map((contact) => {
        const detail =
          contact.relatedListings.length > 0
            ? `Linked to ${contact.relatedListings[0]}`
            : contact.type;
        const tone =
          contact.type === "Seller"
            ? "success"
            : contact.type === "Buyer"
            ? "info"
            : contact.type === "Legal"
            ? "warning"
            : "neutral";
        return {
          label: contact.name,
          detail,
          tone
        };
      })
    },
    {
      key: "onboarding",
      title: "Onboarding",
      count: counts.checkout || 0,
      target: CRM_PIPELINE_TARGETS.onboarding,
      progress: Math.min(100, Math.round(((counts.checkout || 0) / CRM_PIPELINE_TARGETS.onboarding) * 100)),
      status:
        counts.checkout >= CRM_PIPELINE_TARGETS.onboarding
          ? "healthy"
          : counts.checkout
          ? "focus"
          : "empty",
      description: "Sellers completing contracts and upfront payments.",
      highlights: sellers
        .filter((seller) => seller.status === "checkout")
        .slice(0, 3)
        .map((seller) => ({
          label: seller.address?.split(",")[0] || seller.contactName || deriveDisplayName(seller.email),
          detail: seller.contractSignedAt ? "Contract signed" : "Awaiting agreement",
          tone: seller.contractSignedAt ? "success" : "warning"
        }))
    },
    {
      key: "active",
      title: "Live listings",
      count: counts.active || 0,
      target: CRM_PIPELINE_TARGETS.active,
      progress: Math.min(100, Math.round(((counts.active || 0) / CRM_PIPELINE_TARGETS.active) * 100)),
      status: counts.active >= CRM_PIPELINE_TARGETS.active ? "healthy" : counts.active ? "focus" : "empty",
      description: "Properties live with marketing and enquiries.",
      highlights: activeListings.slice(0, 3).map((listing) => {
        const viewingsCount = viewingsByListing[listing.id] || 0;
        const offersCount = offersByListing[listing.id] || 0;
        let detail = `${viewingsCount} viewing${viewingsCount === 1 ? "" : "s"} scheduled`;
        if (offersCount) {
          detail += ` • ${offersCount} offer${offersCount === 1 ? "" : "s"}`;
        }
        return {
          label: listing.title,
          detail,
          tone: offersCount ? "success" : viewingsCount ? "neutral" : "warning"
        };
      })
    }
  ];

  const boardRequests = sellers
    .map((seller) => {
      const pref = seller.boardPreference || "undecided";
      if (pref === "no") return null;
      const listing = seller.activeListingId ? listingMap[seller.activeListingId] : null;
      const requestedAt = seller.boardRequestUpdatedAt ? new Date(seller.boardRequestUpdatedAt) : null;
      if (pref === "undecided") {
        return {
          id: `${seller.email}-decision`,
          seller,
          listing,
          status: "Awaiting seller decision",
          tone: "warning",
          requestedAt
        };
      }
      if (!seller.boardInstallBooked) {
        return {
          id: `${seller.email}-schedule`,
          seller,
          listing,
          status: "Schedule install",
          tone: "warning",
          requestedAt
        };
      }
      return {
        id: `${seller.email}-booked`,
        seller,
        listing,
        status: `Install booked ${formatShortDate(new Date(seller.boardInstallBooked))}`,
        tone: "neutral",
        requestedAt
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aTime = a.requestedAt ? a.requestedAt.getTime() : Number.MAX_SAFE_INTEGER;
      const bTime = b.requestedAt ? b.requestedAt.getTime() : Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });

  const docChecks = sellers
    .map((seller) => {
      const missing = [];
      if (!seller.ownershipDocReceived && !seller.ownershipDoc) missing.push("Proof of ownership");
      if (!seller.idDocsVerified) missing.push("ID / AML");
      if (!seller.epcDocument) missing.push("EPC");
      if (!missing.length) return null;
      return {
        id: `${seller.email}-docs`,
        seller,
        missing,
        tone: missing.length > 1 ? "warning" : "neutral"
      };
    })
    .filter(Boolean);

  const offerActivities = offers.slice(0, 4).map((offer) => {
    const tone = (offer.status || "").toLowerCase().includes("await") ? "warning" : "info";
    return {
      id: `offer-${offer.id}`,
      title: `Offer ${offer.status?.toLowerCase()}`,
      detail: `${offer.buyerLabel} • ${offer.listing?.title || offer.listingId}`,
      timestamp: offer.updatedAt || new Date(),
      tone
    };
  });

  // Add automation event activities
  const automationActivities = [];
  
  // Add viewing reminder events for upcoming viewings
  viewings.slice(0, 2).forEach((viewing) => {
    if (viewing.start && new Date(viewing.start) > new Date()) {
      const reminderTime = new Date(new Date(viewing.start).getTime() - 24*60*60*1000);
      if (reminderTime > new Date()) {
        automationActivities.push({
          id: `auto-viewing-reminder-${viewing.id}`,
          title: '🤖 Auto-reminder scheduled',
          detail: `Viewing reminder for ${viewing.buyerLabel} will be sent 24h before`,
          timestamp: reminderTime,
          tone: 'automation'
        });
      }
    }
  });
  
  // Add offer notification events for recent offers
  offers.slice(0, 2).forEach((offer) => {
    if (offer.updatedAt) {
      automationActivities.push({
        id: `auto-offer-notification-${offer.id}`,
        title: '🤖 Auto-notification sent',
        detail: `${offer.status} notification sent to ${offer.status?.toLowerCase().includes('received') ? 'seller' : 'buyer'}`,
        timestamp: offer.updatedAt,
        tone: 'automation'
      });
    }
  });

  const viewingActivities = viewings.slice(0, 4).map((viewing) => ({
    id: `viewing-${viewing.id}`,
    title: `${viewing.status} viewing`,
    detail: `${viewing.buyerLabel} • ${viewing.listing?.title || viewing.listingId}`,
    timestamp: viewing.occursAt || new Date(),
    tone: (viewing.status || "").toLowerCase().includes("await") ? "warning" : "success"
  }));

  const baseActivities = CRM_ACTIVITY_BASELINE.map((entry) => ({
    ...entry,
    timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date()
  }));

  const activity = [...baseActivities, ...offerActivities, ...viewingActivities, ...automationActivities]
    .sort((a, b) => (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0))
    .slice(0, 10);

  return {
    metrics,
    pipeline,
    viewings,
    offers,
    boardRequests,
    docChecks,
    activity,
    activeListings,
    counts,
    sellers,
    contacts,
    userProfiles,
    preferences: {
      viewingStyle: sellers[0]?.viewingStyle || "hosted"
    }
  };
}

function useCrmData(extraDeps = []) {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handle = (event) => {
      // Refresh on window focus or storage changes
      if (!event || !("key" in event)) {
        setVersion((n) => n + 1);
        return;
      }
      // Storage keys no longer relevant - localStorage removed for business data
    };
    window.addEventListener("storage", handle);
    window.addEventListener("focus", handle);
    return () => {
      window.removeEventListener("storage", handle);
      window.removeEventListener("focus", handle);
    };
  }, []);

  return useMemo(() => buildDashboardData(), [version, ...extraDeps]);
}

function badgeClassForStatus(statusLabel) {
  const normalised = (statusLabel || "").toLowerCase();
  if (normalised.includes("confirm") && normalised.includes("await")) {
    return "border border-amber-400/40 text-amber-200";
  }
  if (normalised.includes("confirm")) {
    return "border border-emerald-400/40 text-emerald-200";
  }
  if (normalised.includes("proof") || normalised.includes("counter")) {
    return "border border-sky-400/40 text-sky-200";
  }
  if (normalised.includes("reject") || normalised.includes("withdraw")) {
    return "border border-rose-400/40 text-rose-200";
  }
  return "border border-white/20/40 text-white/80";
}

function readSessionValue(key, fallback = null) {
  if (typeof window === "undefined" || !window.sessionStorage) return fallback;
  try {
    const value = window.sessionStorage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
}

function writeSessionValue(key, value) {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    if (value === undefined || value === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, value);
    }
  } catch {
    // ignore storage quota failures
  }
}

function removeSessionValue(key) {
  writeSessionValue(key, null);
}

function exportInterestList(entries = []) {
  if (!entries.length || typeof window === "undefined") return;
  const rows = entries
    .map((entry) => {
      const submitted = entry.createdAt
        ? new Date(entry.createdAt).toISOString()
        : "";
      return `"${(entry.email || "").replace(/"/g, '""')}","${(entry.name || "").replace(/"/g, '""')}","${submitted}"`;
    })
    .join("\n");
  const csv = ["Email,Name,Submitted", rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `home-interest-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function CrmLayout({ session, navItems = NAV_ITEMS, title = "Internal CRM" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const crumbLabelMap = {
    crm: "CRM",
    dashboard: "Dashboard",
    pipeline: "Pipeline",
    listings: "Listings",
    contacts: "Contacts",
    viewings: "Viewings",
    offers: "Offers",
    sales: "Sales",
    letters: "Letters",
    settings: "Settings",
    admin: "Admin",
    "deleted-contacts": "Deleted contacts"
  };
  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const crumbs = [];
    let built = "";
    for (const segment of segments) {
      built += `/${segment}`;
      crumbs.push({
        label: crumbLabelMap[segment] || segment,
        path: built
      });
    }
    return crumbs;
  }, [location.pathname]);
  const isAdmin = session?.roles?.includes(Roles.ADMIN);
  const visibleNavItems = useMemo(
    () => navItems.filter((item) => !item.adminOnly || isAdmin),
    [navItems, isAdmin]
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchActiveIndex, setSearchActiveIndex] = useState(0);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleShortcut = (event) => {
      if (!event || typeof event.key !== "string") return;
      const key = event.key.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === "k") {
        const target = event.target;
        const tagName = target?.tagName?.toLowerCase();
        if (tagName === "input" || tagName === "textarea" || target?.isContentEditable) return;
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [searchOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (searchOpen) {
      setSearchQuery("");
      setSearchActiveIndex(0);
      setTimeout(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      }, 0);
      document.body.style.setProperty("overflow", "hidden");
    } else {
      document.body.style.removeProperty("overflow");
    }
    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [searchOpen]);

  useEffect(() => {
    setSearchActiveIndex(0);
  }, [searchQuery]);

  const searchData = useCrmData([searchOpen]);

  const searchResults = useMemo(() => {
    const items = [];
    const pushItem = (item) => {
      const keywords = item.keywords || [];
      const haystack = [item.title, item.description, item.badge, ...keywords]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      items.push({ ...item, keywords, haystack });
    };

    (searchData.boardRequests || []).forEach((request) => {
      pushItem({
        id: `board-${request.id}`,
        type: "Board request",
        title: request.seller?.contactName || deriveDisplayName(request.seller?.email),
        badge: request.listing?.title || request.seller?.address || "Board install",
        description: request.status,
        meta: request.requestedAt ? formatRelative(request.requestedAt) : "New",
        priority: 0,
        keywords: [
          request.seller?.email,
          request.seller?.postcode,
          request.listing?.area,
          request.status
        ],
        route: "/crm/dashboard"
      });
    });

    (searchData.docChecks || []).forEach((item) => {
      pushItem({
        id: `doc-${item.id}`,
        type: "Compliance",
        title: item.seller?.contactName || deriveDisplayName(item.seller?.email),
        badge: item.missing.length === 1 ? "1 outstanding" : `${item.missing.length} outstanding`,
        description: item.missing.join(", "),
        meta: item.seller?.postcode || "",
        priority: 1,
        keywords: [
          item.seller?.email,
          item.seller?.address,
          ...item.missing
        ],
        route: "/crm/dashboard"
      });
    });

    (searchData.offers || []).forEach((offer) => {
      pushItem({
        id: `offer-${offer.id}`,
        type: "Offer",
        title: offer.listing?.title || "Offer",
        badge: offer.status,
        description: `${offer.buyerLabel || "Buyer"} • ${offer.amountDisplay}`,
        meta: offer.updatedAt ? formatRelative(offer.updatedAt) : "",
        priority: 2,
        keywords: [
          offer.buyerLabel,
          offer.listing?.area,
          offer.seller?.contactName,
          offer.status,
          offer.amountDisplay
        ],
        route: "/crm/offers",
        onSelect: () => {
          writeSessionValue("crm:focusOffer", offer.id);
          navigate("/crm/offers");
        }
      });
    });

    (searchData.viewings || []).forEach((viewing) => {
      pushItem({
        id: `viewing-${viewing.id}`,
        type: "Viewing",
        title: viewing.listing?.title || "Viewing",
        badge: formatShortDate(viewing.occursAt),
        description: `${viewing.buyerLabel || "Buyer"} • ${formatShortTime(viewing.occursAt)}`,
        meta: viewing.status,
        priority: 3,
        keywords: [
          viewing.buyerLabel,
          viewing.listing?.area,
          viewing.seller?.contactName,
          viewing.status,
          viewing.notes
        ],
        route: "/crm/viewings",
        onSelect: () => {
          writeSessionValue("crm:focusViewing", viewing.id);
          navigate("/crm/viewings");
        }
      });
    });

    (searchData.sellers || []).forEach((seller) => {
      pushItem({
        id: `seller-${seller.email}`,
        type: "Seller",
        title: seller.contactName || deriveDisplayName(seller.email),
        badge: seller.status ? seller.status.charAt(0).toUpperCase() + seller.status.slice(1) : "Seller",
        description: seller.address || seller.postcode || "Address pending",
        meta: seller.planKey ? `${seller.planKey === "premier" ? "Premier" : "Core"} plan` : "",
        priority: seller.status === "prospect" ? 2.5 : 3.5,
        keywords: [
          seller.email,
          seller.postcode,
          seller.planKey,
          seller.status,
          seller.address
        ],
        route: "/crm/dashboard",
        onSelect: () => {
          writeSessionValue("crm:focusSeller", seller.email);
          navigate("/crm/dashboard");
        }
      });
    });

    LISTINGS.forEach((listing) => {
      pushItem({
        id: `listing-${listing.id}`,
        type: "Listing",
        title: listing.title,
        badge: listing.area || listing.postcode || "Listing",
        description: listing.address || listing.postcode || "",
        meta: formatCurrency(listing.price),
        priority: 4,
        keywords: [
          listing.id,
          listing.postcode,
          listing.address,
          listing.type,
          ...(listing.features || [])
        ],
        route: "/crm/listings",
        onSelect: () => {
          writeSessionValue("crm:targetListing", listing.id);
          navigate("/crm/listings");
        }
      });
    });

    const query = searchQuery.trim().toLowerCase();
    const tokens = query ? query.split(/\s+/).filter(Boolean) : [];
    const sortedItems = items.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.title.localeCompare(b.title);
    });
    const filtered = tokens.length
      ? sortedItems.filter((item) => tokens.every((token) => item.haystack.includes(token)))
      : sortedItems;
    return filtered.slice(0, 20);
  }, [navigate, searchData, searchQuery]);

  useEffect(() => {
    setSearchActiveIndex((idx) => {
      if (!searchResults.length) return 0;
      return Math.min(idx, searchResults.length - 1);
    });
  }, [searchResults.length]);

  const handleResultSelect = (item) => {
    if (!item) return;
    setSearchOpen(false);
    if (typeof item.onSelect === "function") {
      item.onSelect();
      return;
    }
    if (item.route) {
      navigate(item.route);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSearchActiveIndex((idx) => Math.min(idx + 1, Math.max(searchResults.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSearchActiveIndex((idx) => Math.max(idx - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      handleResultSelect(searchResults[searchActiveIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setSearchOpen(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-black text-white">
      <aside className="hidden lg:flex w-64 flex-shrink-0 flex-col border-r border-white/5 bg-black/60 backdrop-blur-2xl">
        <div className="px-6 py-6 border-b border-white/5">
          <div className="text-xs uppercase tracking-[0.35em] text-white/40">Home AI</div>
          <div className="mt-2 text-xl font-light text-white">Control Room</div>
        </div>
        <nav className="flex-1 space-y-1 px-4 py-6">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition",
                  isActive
                    ? "bg-white/10 text-white border-l-2 border-white/40"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                ].join(" ")
              }
            >
              <NavItemIcon label={item.label} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-white/5 text-xs text-white/40">
          <div className="text-[10px] uppercase tracking-[0.35em] mb-1">Logged in</div>
          <div className="text-sm text-white/70 truncate">{session?.email || "agent@home.local"}</div>
        </div>
      </aside>
      <div className="flex flex-1 flex-col">
        <header className="border-b border-white/5 bg-black/40 backdrop-blur-2xl">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-8 py-5">
            <div className="flex items-center gap-4">
              <span className="text-xl font-light text-white">{title}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <button
                className="rounded-full border border-white/30 px-4 py-2 text-xs uppercase tracking-[0.35em] text-white/70 hover:bg-white/10 hover:text-white transition"
                onClick={() => setSearchOpen(true)}
              >
                Search (⌘K)
              </button>
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
                <span className="text-xs text-white/60">Operational</span>
              </div>
            </div>
          </div>
        </header>
        {breadcrumbs.length > 0 && (
          <div className="border-b border-white/5 bg-black/40 backdrop-blur">
            <div className="mx-auto flex max-w-6xl items-center gap-3 px-8 py-3 text-xs text-white/60">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-full border border-white/20 px-3 py-1 text-[0.6rem] uppercase tracking-[0.35em] text-white/60 transition hover:border-white/40 hover:text-white/80"
              >
                Back
              </button>
              <div className="flex flex-wrap items-center gap-2">
                {breadcrumbs.map((crumb, idx) => (
                  <React.Fragment key={crumb.path}>
                    <button
                      type="button"
                      onClick={() => navigate(crumb.path)}
                      className="text-[0.65rem] text-white/70 underline-offset-2 transition hover:text-white"
                    >
                      {crumb.label}
                    </button>
                    {idx < breadcrumbs.length - 1 && (
                      <span className="text-white/30">/</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
        <main className="flex-1 overflow-y-auto bg-black">
          <div className="mx-auto w-full max-w-6xl px-8 py-8">
            <Outlet context={{ session }} />
          </div>
        </main>
      </div>

      {searchOpen && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="mx-auto mt-24 w-full max-w-3xl rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-white/10 px-6 py-5">
              <div className="flex items-center justify-between text-xs text-white/40">
                <span className="uppercase tracking-[0.35em]">Search</span>
                <span className="uppercase tracking-[0.35em]">Esc to close</span>
              </div>
              <div className="mt-4 flex items-center gap-3 rounded-full border border-white/20 bg-black/60 px-5 py-3.5">
                <span className="text-sm text-white/40">🔎</span>
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search listings, sellers, offers, viewings..."
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="text-xs text-white/50 hover:text-white/80"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-[420px] overflow-y-auto divide-y divide-white/10">
              {searchResults.length === 0 ? (
                <div className="px-6 py-10 text-sm text-white/60">
                  No matches for <span className="text-white/70">&ldquo;{searchQuery}&rdquo;</span>. Try different keywords.
                </div>
              ) : (
                searchResults.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleResultSelect(item)}
                    onMouseEnter={() => setSearchActiveIndex(index)}
                    className={
                      "flex w-full items-start gap-3 px-6 py-4 text-left transition " +
                      (index === searchActiveIndex ? "bg-white/10" : "hover:bg-white/5")
                    }
                  >
                    <div className="flex flex-shrink-0 flex-col items-start gap-2">
                      <span
                        className={
                          "rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.35em] " +
                          (SEARCH_TYPE_STYLES[item.type] || "border-white/20 bg-white/10 text-white/80")
                        }
                      >
                        {item.type}
                      </span>
                      {item.meta && (
                        <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">
                          {item.meta}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{item.title}</p>
                      {item.description && (
                        <p className="mt-1 text-xs text-white/60">{item.description}</p>
                      )}
                      {item.badge && (
                        <p className="mt-2 text-xs text-white/60">
                          {item.badge}
                        </p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CrmPlaceholder({ title, description }) {
  return (
    <div className="max-w-4xl space-y-6">
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.35em] text-white/40">Coming soon</p>
        <h1 className="text-4xl font-light text-white">{title}</h1>
        <p className="text-sm text-white/60 max-w-2xl">{description}</p>
      </div>
      <div className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl p-8 text-sm leading-relaxed text-white/70">
        <p>
          This area will soon host the full workflow for {title.toLowerCase()}. Listings is the first fully implemented flow; the others are stubs.
        </p>
      </div>
    </div>
  );
}

export function CrmDashboardPage() {
  const navigate = useNavigate();
  const data = useCrmData();
  const { metrics, pipeline, viewings, activity, activeListings, contacts } = data;
  const [interestLeads, setInterestLeads] = useState([]);
  const [interestLoading, setInterestLoading] = useState(true);
  const [interestError, setInterestError] = useState("");
  const [interestPage, setInterestPage] = useState(1);
  const INTEREST_PAGE_SIZE = 6;

  useEffect(() => {
    let active = true;
    setInterestLoading(true);
    setInterestPage(1);
    getInterestLeads()
      .then((entries) => {
        if (!active) return;
        const sorted = [...entries].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setInterestLeads(sorted);
      })
      .catch((error) => {
        if (!active) return;
        setInterestError(error?.message || "Unable to load interest leads");
      })
      .finally(() => {
        if (!active) return;
        setInterestLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const offersMetric = metrics.find((metric) => metric.key === "offers") || {};
  const viewingsMetric = metrics.find((metric) => metric.key === "viewings") || {};

  const headerSummary = [
    `${activeListings.length} live listing${activeListings.length === 1 ? "" : "s"}`,
    `${contacts.length} client${contacts.length === 1 ? "" : "s"}`,
    `${viewingsMetric.value ?? 0} viewing${viewingsMetric.value === 1 ? "" : "s"} this week`,
    `${offersMetric.value ?? 0} offer${offersMetric.value === 1 ? "" : "s"} in review`
  ].join(" • ");

  const upcoming = viewings.filter((viewing) => viewing.occursAt && viewing.occursAt >= new Date()).slice(0, 4);
  const recentActivity = activity.slice(0, 5);

  const quickActions = [
    { label: "New listing", detail: "Launch a property", action: () => navigate("/crm/listings") },
    { label: "Add contact", detail: "Log a buyer or seller", action: () => navigate("/crm/contacts") },
    { label: "Book viewing", detail: "Schedule a showing", action: () => navigate("/crm/viewings") },
    { label: "Log offer", detail: "Capture negotiations", action: () => navigate("/crm/offers") }
  ];

  const openActiveListings = () => {
    writeSessionValue("crm:listingsFilter", "ACTIVE");
    navigate("/crm/listings");
  };

  const pipelineColor = (status) => {
    if (status === "healthy") return "bg-emerald-400";
    if (status === "focus") return "bg-amber-400";
    return "bg-white/30";
  };

  const toneDotColor = (tone) => {
    if (tone === "success") return "bg-emerald-400";
    if (tone === "warning") return "bg-amber-400";
    if (tone === "info") return "bg-sky-400";
    if (tone === "automation") return "bg-purple-400";
    return "bg-white/30";
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl p-8 shadow-[0_25px_80px_rgba(0,0,0,0.65)]">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.35em] text-white/40">Control Room</p>
            <h1 className="text-4xl font-light text-white">Dashboard</h1>
            <p className="text-sm text-white/60 max-w-2xl">High-level pipeline health and today&apos;s momentum.</p>
            <p className="text-[10px] uppercase tracking-[0.5em] text-white/30 mt-4">{headerSummary}</p>
          </div>
          <div className="flex flex-col gap-2 rounded-3xl border border-white/10 bg-white/5 px-6 py-5 text-center backdrop-blur hover:border-white/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.15)] transition-all duration-300">
            <span className="text-xs uppercase tracking-[0.35em] text-white/40">Active listings</span>
            <strong className="text-3xl font-light text-white">{activeListings.length}</strong>
            <span className="text-xs text-white/60">Currently live</span>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          {quickActions.map((item) => (
            <button
              key={item.label}
              onClick={item.action}
              className="rounded-2xl border border-white/10 bg-black/40 px-5 py-3.5 text-left hover:bg-white/5 hover:border-white/30 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300 backdrop-blur"
            >
              <span className="block text-sm font-medium text-white">{item.label}</span>
              <span className="block text-xs text-white/60 mt-1">{item.detail}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={openActiveListings}
            className="rounded-2xl border border-white/20 bg-white/5 px-5 py-3.5 text-left hover:bg-white/10 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all duration-300 backdrop-blur"
          >
            <span className="block text-sm font-medium text-white">Active listings</span>
            <span className="block text-xs text-white/60 mt-1">View live instructions</span>
          </button>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => (
          <div key={metric.key} className="rounded-3xl border border-white/10 bg-black/60 backdrop-blur-2xl p-6 hover:border-white/30 hover:shadow-[0_0_25px_rgba(255,255,255,0.1)] transition-all duration-300">
            <p className="text-xs uppercase tracking-[0.35em] text-white/40">{metric.label}</p>
            <p className="text-3xl font-light text-white mt-4">{metric.value ?? 0}</p>
            <p className="text-xs text-white/60 mt-2">{metric.meta}</p>
          </div>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.15fr,0.85fr]">
        <div className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl p-8 space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-2">
              <h2 className="text-2xl font-light text-white">Pipeline health</h2>
              <p className="text-sm text-white/60">Progress toward client, onboarding and live targets.</p>
            </div>
            <button
              onClick={() => navigate("/crm/pipeline")}
              className="rounded-full border border-white/30 px-4 py-2 text-xs uppercase tracking-[0.35em] text-white/70 hover:bg-white/10 hover:text-white hover:border-white/60 hover:shadow-[0_0_15px_rgba(255,255,255,0.15)] transition-all duration-300"
            >
              View pipeline
            </button>
          </div>
          <div className="space-y-5">
            {pipeline.map((stage) => (
              <div key={stage.key}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-white">{stage.title}</p>
                  <span className="text-[11px] uppercase tracking-[0.35em] text-white/40">{stage.status}</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-white/10">
                  <div
                    className={`${pipelineColor(stage.status)} h-2 rounded-full transition-all`}
                    style={{ width: `${stage.progress}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-white/60">{stage.description}</p>
                {(stage.highlights || []).slice(0, 2).map((highlight) => (
                  <p key={highlight.label} className="text-[11px] text-white/60">
                    {highlight.label} • {highlight.detail}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl p-8 space-y-6">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-light text-white">Upcoming viewings</h2>
              <button
                onClick={() => navigate("/crm/viewings")}
                className="rounded-full border border-white/30 px-4 py-2 text-xs uppercase tracking-[0.35em] text-white/70 hover:bg-white/10 hover:text-white hover:border-white/60 hover:shadow-[0_0_15px_rgba(255,255,255,0.15)] transition-all duration-300"
              >
                Full schedule
              </button>
            </div>
            <div className="space-y-4">
              {upcoming.length === 0 && (
                <div className="rounded-3xl border border-white/10 bg-black/40 px-6 py-8 text-sm text-white/60 text-center">
                  No viewings scheduled for the next few days.
                </div>
              )}
              {upcoming.map((viewing) => (
                <div
                  key={viewing.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/5 bg-black/50 px-4 py-3 text-sm hover:border-white/20 hover:bg-black/60 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300 cursor-pointer"
                >
                  <div>
                    <p className="text-xs text-white/60">{formatShortDate(viewing.occursAt)}</p>
                    <p className="text-xs text-white/70 font-medium">{formatShortTime(viewing.occursAt)}</p>
                  </div>
                  <div className="flex-1 min-w-[170px]">
                    <p className="font-semibold text-white">{viewing.listing?.title || "Listing TBD"}</p>
                    <p className="text-xs text-white/60">{viewing.buyerLabel}</p>
                  </div>
                  <div className="text-right min-w-[110px]">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] text-white/80 ${badgeClassForStatus(
                        viewing.status
                      )}`}
                    >
                      {viewing.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl p-8 space-y-6">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-light text-white">Activity feed</h2>
              <button
                onClick={() => navigate("/crm/listings")}
                className="rounded-full border border-white/30 px-4 py-2 text-xs uppercase tracking-[0.35em] text-white/70 hover:bg-white/10 hover:text-white hover:border-white/60 hover:shadow-[0_0_15px_rgba(255,255,255,0.15)] transition-all duration-300"
              >
                Export feed
              </button>
            </div>
            <div className="space-y-3">
              {recentActivity.length === 0 && (
                <div className="rounded-2xl border border-white/10 bg-black/50 px-4 py-5 text-sm text-white/60">
                  Activity will appear here once actions are recorded.
                </div>
              )}
              {recentActivity.map((entry) => (
                <div
                  key={entry.id}
                  className="flex flex-col gap-2 rounded-3xl border border-white/10 bg-black/40 px-5 py-4 text-sm backdrop-blur hover:border-white/20 hover:bg-black/50 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-white/60">
                      <span className={`h-2.5 w-2.5 rounded-full ${toneDotColor(entry.tone)}`} />
                      {entry.source || "system"}
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.35em] text-white/40">
                      {formatRelative(entry.timestamp)}
                    </span>
                  </div>
                  <p className="text-sm text-white">{entry.title}</p>
                  {entry.detail && <p className="text-xs text-white/60">{entry.detail}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl p-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-2">
            <h2 className="text-2xl font-light text-white">Interest submissions</h2>
            <p className="text-sm text-white/60">
              Compiled emails from the "Register interest" page — contact-ready for the CRM team.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/crm/contacts")}
              className="rounded-full border border-white/30 px-4 py-2 text-xs uppercase tracking-[0.35em] text-white/70 hover:bg-white/10 hover:text-white hover:border-white/60 hover:shadow-[0_0_15px_rgba(255,255,255,0.15)] transition-all duration-300"
            >
              View contacts
            </button>
            <button
              onClick={() => exportInterestList(interestLeads)}
              disabled={interestLoading || !interestLeads.length}
              className="rounded-full bg-white text-black px-4 py-2 text-xs uppercase tracking-[0.35em] font-medium shadow-[0_12px_30px_rgba(255,255,255,0.2)] hover:bg-white/90 hover:shadow-[0_12px_40px_rgba(255,255,255,0.35)] transition-all duration-300 disabled:opacity-40"
            >
              Export list
            </button>
          </div>
        </div>
        <div className="space-y-3">
          {interestLoading && (
            <div className="rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white/60">
              Loading interest submissions…
            </div>
          )}
          {!interestLoading && interestError && (
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
              {interestError}
            </div>
          )}
          {!interestLoading && !interestError && interestLeads.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white/60">
              No registrations yet — share the landing page to build your pipeline.
            </div>
          )}
          {!interestLoading &&
            !interestError &&
            interestLeads.length > 0 &&
            interestLeads
              .slice((interestPage - 1) * INTEREST_PAGE_SIZE, interestPage * INTEREST_PAGE_SIZE)
              .map((entry) => (
                <div
                  key={entry.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white/80 hover:border-white/20 hover:bg-black/50 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300 cursor-pointer"
                >
                  <div>
                    <p className="font-semibold text-white">{entry.email}</p>
                    {entry.name && <p className="text-xs text-white/60">{entry.name}</p>}
                  </div>
                  <p className="text-xs uppercase tracking-[0.35em] text-white/40">
                    {formatShortDateTime(new Date(entry.createdAt))}
                  </p>
                </div>
              ))}
        </div>
        <div className="flex items-center justify-between text-xs text-white/60">
          <span>
            Showing {Math.min(interestLeads.length, interestPage * INTEREST_PAGE_SIZE)} of {interestLeads.length} submissions
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setInterestPage((current) => Math.max(1, current - 1))}
              disabled={interestPage === 1}
              className="rounded-lg border border-white/10 px-3 py-1 disabled:opacity-40"
            >
              Prev
            </button>
            <span>
              Page {interestPage} of {Math.max(1, Math.ceil(interestLeads.length / INTEREST_PAGE_SIZE))}
            </span>
            <button
              onClick={() =>
                setInterestPage((current) =>
                  Math.min(Math.max(1, Math.ceil(interestLeads.length / INTEREST_PAGE_SIZE)), current + 1)
                )
              }
              disabled={interestPage >= Math.max(1, Math.ceil(interestLeads.length / INTEREST_PAGE_SIZE))}
              className="rounded-lg border border-white/10 px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}


export function CrmListingsPage() {
  const navigate = useNavigate();
  const storedFilter = readSessionValue("crm:listingsFilter");
  const [statusFilter, setStatusFilter] = useState(() => {
    if (!storedFilter) return "all";
    const normalized = CRM_STATUS_OPTIONS.find((option) => option.value?.toLowerCase() === storedFilter.toLowerCase());
    return normalized ? normalized.value : storedFilter;
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [listingsData, setListingsData] = useState({ items: [], total: 0 });
  const [listingsLoading, setListingsLoading] = useState(false);
  const [listingsError, setListingsError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showNewForm, setShowNewForm] = useState(false);

  useEffect(() => {
    const stored = readSessionValue("crm:listingsFilter");
    if (stored) {
      const normalized = CRM_STATUS_OPTIONS.find((option) => option.value?.toLowerCase() === stored.toLowerCase());
      setStatusFilter(normalized ? normalized.value : stored);
      removeSessionValue("crm:listingsFilter");
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setListingsLoading(true);
    setListingsError("");
    getListings({
      status: statusFilter === "all" ? undefined : statusFilter,
      q: search || undefined,
      page,
      pageSize: CRM_LISTINGS_PAGE_SIZE
    })
      .then((data) => {
        if (!controller.signal.aborted) {
          const normalized = {
            items: Array.isArray(data?.items) ? data.items : [],
            total: typeof data?.total === "number" ? data.total : 0
          };
          setListingsData(normalized);
          if (!Array.isArray(data?.items)) {
            setListingsError("Listings API returned an invalid response shape.");
          }
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setListingsError(err.message || "Failed to load listings");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setListingsLoading(false);
        }
      });
    return () => controller.abort();
  }, [statusFilter, search, page, refreshKey]);

  const listingItems = Array.isArray(listingsData?.items) ? listingsData.items : [];
  const listingsTotal = typeof listingsData?.total === "number" ? listingsData.total : listingItems.length;

  const listingCards = listingItems.map((listing) => {
    const addressParts = [
      listing.address?.line1,
      listing.address?.line2,
      listing.address?.city,
      listing.address?.postcode
    ]
      .filter(Boolean);
    const heroCandidate = Array.isArray(listing.media) && listing.media.length ? listing.media[0] : null;
    const heroUrl = heroCandidate?.url || heroCandidate?.src || heroCandidate?.thumbnail || null;
    const viewingsCount = listing._count?.viewings || 0;
    const offersCount = listing._count?.offers || 0;
    const ownerLabel = listing.ownerContact?.name || listing.ownerContact?.emails?.[0] || "Contact";
    const highlightLabel =
      Array.isArray(listing.features) && listing.features.length
        ? listing.features.slice(0, 3).join(" • ")
        : "";
    const portalLabel =
      listing.status === "ACTIVE"
        ? "Live on portals"
        : listing.status === "UNDER_OFFER"
        ? "Under offer"
        : listing.status === "COMING_SOON"
        ? "Launch ready"
        : "Draft";
    const badgeClass =
      listing.status === "ACTIVE"
        ? "border-emerald-400/40 text-emerald-200"
        : listing.status === "UNDER_OFFER"
        ? "border-amber-400/40 text-amber-200"
        : listing.status === "COMING_SOON"
        ? "border-sky-400/40 text-sky-200"
        : "border-white/10 text-white/60";
    return {
      id: listing.id,
      refCode: listing.refCode,
      title: listing.address?.line1 || listing.refCode,
      address: addressParts.join(", ") || listing.refCode,
      hero: heroUrl,
      price: listing.priceGuide,
      status: listing.status,
      owner: ownerLabel,
      viewingsCount,
      offersCount,
      portalLabel,
      highlights: highlightLabel,
      description: listing.description || listing.address?.line2 || "",
      badgeClass
    };
  });

  const totalPages = Math.max(1, Math.ceil(listingsTotal / CRM_LISTINGS_PAGE_SIZE));

  const handleNewListingCreated = () => {
    setShowNewForm(false);
    setPage(1);
    setRefreshKey((value) => value + 1);
  };

  return (
    <div className="space-y-8">
      <header className="space-y-3">
        <p className="text-xs uppercase tracking-[0.35em] text-white/40">Properties</p>
        <h1 className="text-4xl font-light text-white">Listings</h1>
        <p className="text-sm text-white/60 max-w-2xl">
          Visual overview of every instruction with quick access to seller and buyer activity.
        </p>
      </header>

      <section className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl p-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-white/20 bg-black/60 px-5 py-3">
            <span className="text-sm text-white/40">🔎</span>
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search address, ref code, description..."
              className="flex-1 bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setPage(1);
              setStatusFilter(event.target.value);
            }}
            className="rounded-full border border-white/30 bg-black/60 px-4 py-2.5 text-sm text-white focus:outline-none"
          >
            {CRM_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowNewForm(true)}
            className="rounded-full border border-white/30 px-4 py-2.5 text-xs uppercase tracking-[0.35em] text-white/70 hover:bg-white/10 hover:text-white transition"
          >
            New listing
          </button>
        </div>
        {listingsError && (
          <div className="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {listingsError}
          </div>
        )}
      </section>

      <section className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
        {listingCards.length === 0 && listingsLoading && (
          <div className="col-span-full rounded-3xl border border-white/10 bg-white/5 px-6 py-10 text-center text-sm text-white/60">
            Loading listings…
          </div>
        )}
        {!listingsLoading && listingCards.length === 0 && (
          <div className="col-span-full rounded-3xl border border-white/10 bg-white/5 px-6 py-10 text-center text-sm text-white/60">
            No listings match your filters.
          </div>
        )}
        {listingCards.map((listing) => (
          <div
            key={listing.id}
            className="flex h-full flex-col overflow-hidden rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl"
          >
            <div className="h-48 w-full overflow-hidden bg-black/20">
              {listing.hero ? (
                <img src={listing.hero} alt={listing.title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-white/40">
                  No media yet
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col gap-4 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-medium text-white">{listing.title}</h2>
                  <p className="text-xs text-white/60 mt-1">{listing.address}</p>
                </div>
                <span className={"rounded-full border px-3 py-1 text-xs uppercase tracking-[0.35em] " + listing.badgeClass}>
                  {STATUS_LABELS[listing.status] || listing.status}
                </span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-white/40">Price guide</p>
                <p className="text-2xl font-light text-white mt-2">{formatCurrency(listing.price)}</p>
              </div>
              <div className="space-y-1 text-xs text-white/60">
                <div>
                  <span className="text-white">Seller:</span> {listing.owner}
                </div>
                <div>
                  <span className="text-white">Activity:</span> {listing.viewingsCount} viewings • {listing.offersCount} offers
                </div>
                <div>
                  <span className="text-white">Portals:</span> {listing.portalLabel}
                </div>
              </div>
              {listing.highlights && (
                <div className="text-[11px] uppercase tracking-[0.35em] text-white/60">
                  Highlights: {listing.highlights}
                </div>
              )}
              <p className="text-sm text-white/60">{listing.description || "No description yet."}</p>
              <div className="mt-auto flex items-center justify-between gap-2 text-sm pt-2">
                <button
                  onClick={() => navigate("/crm/offers")}
                  className="rounded-full border border-white/20 px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70 hover:bg-white/10 hover:text-white transition"
                >
                  Offers
                </button>
                <button
                  onClick={() => navigate(`/crm/listings/${listing.id}`)}
                  className="rounded-full border border-white/30 px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70 hover:bg-white/10 hover:text-white transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (listing.hero) {
                      window.open(listing.hero, "_blank");
                    }
                  }}
                  className="text-xs text-white/50 hover:text-white/80 transition"
                >
                  Assets →
                </button>
              </div>
            </div>
          </div>
        ))}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-white/60">
        <span>
          Showing {listingCards.length} of {listingsData.total} listing{listingsData.total === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1}
            className="rounded-lg border border-white/10 px-3 py-1 disabled:opacity-30"
          >
            Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page >= totalPages}
            className="rounded-lg border border-white/10 px-3 py-1 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      </div>

      <NewListingModal open={showNewForm} onClose={() => setShowNewForm(false)} onCreated={handleNewListingCreated} />
    </div>
  );
}

export function CrmListingDetailPage() {
  const { session } = useOutletContext() || {};
  const { contacts } = useCrmData();
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [viewings, setViewings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [negotiatorEmail, setNegotiatorEmail] = useState("");
  const isAdminOrAgent = session?.roles?.includes(Roles.ADMIN) || session?.roles?.includes(Roles.AGENT);
  const canEditListing = isAdminOrAgent || session?.roles?.includes(Roles.SELLER);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([getListing(id), getListingViewings(id)])
      .then(([listingRes, viewingsRes]) => {
        if (cancelled) return;
        setListing(listingRes);
        setOwnerEmail(listingRes.ownerContact?.emails?.[0] || listingRes.ownerContact?.email || "");
        setNegotiatorEmail(listingRes.negotiator?.email || "");
        setViewings(viewingsRes);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Unable to load listing");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const heroImage = useMemo(() => {
    if (!listing) return null;
    const candidate = Array.isArray(listing.media) ? listing.media[0] : null;
    if (!candidate) return null;
    return candidate.url || candidate.src || candidate.thumbnail || null;
  }, [listing]);

  const summaryFeatures = (listing?.features || []).slice(0, 3).join(" · ");
  const contactOptions = contacts.map((contact) => ({
    email: contact.email,
    label: `${contact.name || contact.email} • ${contact.type || "Contact"}`
  }));

  const handleContactUpdate = (type, value) => {
    if (type === "owner") {
      setOwnerEmail(value);
    } else {
      setNegotiatorEmail(value);
    }
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-10 text-center text-sm text-white/70">
        Loading listing…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-3xl border border-rose-500/30 bg-rose-500/10 px-6 py-10 text-sm text-rose-100">
        {error}
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-10 text-sm text-white/60">
        Listing not found.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">Live listing</p>
          <h1 className="text-3xl font-semibold text-white">{listing.address?.line1 || listing.refCode}</h1>
          <p className="text-sm text-white/60">
            {listing.address?.city}, {listing.address?.postcode}
          </p>
          <p className="text-xs uppercase tracking-[0.3em] text-white/60">
            {STATUS_LABELS[listing.status] || listing.status}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate("/crm/listings")}
            className="rounded-2xl border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/10 transition"
          >
            Back to listings
          </button>
          {canEditListing && (
            <button
              type="button"
              onClick={() => alert("Draft changes saved (simulated).")}
              className="rounded-2xl border border-emerald-400/40 bg-emerald-500/80 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 transition"
            >
              Save updates
            </button>
          )}
        </div>
      </header>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-5 space-y-5">
        <div className="grid gap-4 lg:grid-cols-[1.2fr,0.8fr]">
          <div className="space-y-4">
            {heroImage ? (
              <img src={heroImage} alt={listing.address?.line1 || listing.refCode} className="h-60 w-full rounded-3xl object-cover" />
            ) : (
              <div className="flex h-60 items-center justify-center rounded-3xl border border-white/10 bg-black text-sm text-white/60">
                No media yet
              </div>
            )}
            <div className="text-xs uppercase tracking-[0.4em] text-white/60">Buyer preview</div>
            <div className="text-sm text-white/70">
              <p className="text-xl font-semibold text-white">{formatCurrency(listing.priceGuide)}</p>
              <p className="text-xs text-white/60">
                {listing.beds} bed · {listing.baths} bath · {listing.tenure || "Tenure TBC"}
              </p>
              {summaryFeatures && <p className="mt-2 text-xs text-white/60">{summaryFeatures}</p>}
            </div>
            <div className="rounded-2xl border border-white/5 bg-black/40 p-4 text-sm text-white/80">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">Showcase copy</p>
              <p className="mt-2">{listing.description || "Add a buyer-friendly description to highlight the space."}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4 space-y-2 text-sm text-white/80">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">Connected contacts</p>
              <p>
                <span className="text-white">Owner:</span> {listing.ownerContact?.name || ownerEmail || "Unassigned"}
              </p>
              <p>
                <span className="text-white">Negotiator:</span> {listing.negotiator?.name || negotiatorEmail || "Unassigned"}
              </p>
            </div>
            {canEditListing && (
              <div className="rounded-2xl border border-white/10 bg-black/40 p-4 space-y-3 text-sm text-white/80">
                <p className="text-xs uppercase tracking-[0.3em] text-white/40">Assign contacts</p>
                <label className="text-xs uppercase tracking-[0.25em] text-white/60">
                  Owner
                  <select
                    value={ownerEmail}
                    onChange={(event) => handleContactUpdate("owner", event.target.value)}
                    className="mt-1 w-full rounded-2xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white focus:outline-none"
                  >
                    <option value="">Unassigned</option>
                    {contactOptions.map((option) => (
                      <option key={option.email} value={option.email}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs uppercase tracking-[0.25em] text-white/60">
                  Negotiator
                  <select
                    value={negotiatorEmail}
                    onChange={(event) => handleContactUpdate("negotiator", event.target.value)}
                    className="mt-1 w-full rounded-2xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white focus:outline-none"
                  >
                    <option value="">Unassigned</option>
                    {contactOptions.map((option) => (
                      <option key={option.email} value={option.email}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4 space-y-2 text-sm text-white/80">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">Attachments</p>
              <div className="flex flex-wrap gap-2">
              {(listing.media || [])
                .slice(0, 3)
                .map((asset, index) => (
                    <span key={index} className="rounded-full border border-white/10 px-3 py-1 text-xs">
                      {asset.label || `Asset ${index + 1}`}
                    </span>
                  ))}
              </div>
              {canEditListing && (
                <div className="flex flex-wrap gap-2">
                  <label className="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-white/60">
                    <input type="file" className="hidden" />
                    Upload photos
                  </label>
                  <label className="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-white/60">
                    <input type="file" className="hidden" />
                    Upload EPC
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Upcoming viewings</h2>
          <span className="text-xs text-white/60">
            Last updated {viewings.length ? formatShortDate(viewings[0]?.start) : "—"}
          </span>
        </div>
        <div className="space-y-3">
          {viewings.length === 0 ? (
            <p className="text-sm text-white/60">No viewings scheduled.</p>
          ) : (
            viewings.slice(0, 4).map((viewing) => (
              <div key={viewing.id} className="rounded-2xl border border-white/10 bg-black/40 p-4 text-sm text-white/80">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">{formatShortDateTime(viewing.start)}</p>
                  <span className="text-xs uppercase tracking-[0.3em] text-white/60">{viewing.status}</span>
                </div>
                <p className="text-xs text-white/60">
                  {viewing.buyer?.name || viewing.buyerLabel || "Buyer"}
                </p>
                <p className="text-xs text-white/60">{viewing.notes || "No notes added."}</p>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function NewListingModal({ open, onClose, onCreated }) {
  const [formState, setFormState] = useState(NEW_LISTING_FORM);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [loadingAIEstimate, setLoadingAIEstimate] = useState(false);
  const [aiEstimate, setAiEstimate] = useState(null);

  useEffect(() => {
    if (open) {
      setFormState(NEW_LISTING_FORM);
      setError("");
      setAiEstimate(null);
    }
  }, [open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleGetAIEstimate = async () => {
    if (!formState.line1 || !formState.city || !formState.postcode) {
      setError("Please enter address details first (Line 1, City, Postcode)");
      return;
    }
    
    setLoadingAIEstimate(true);
    setError("");
    try {
      // Note: This would call the backend AI valuation endpoint
      // For now, show UI that capability exists
      setAiEstimate({
        estimate: 450000,
        range: { min: 420000, max: 480000 },
        confidence: "medium",
        note: "Based on market data and property details"
      });
      setFormState(prev => ({ ...prev, priceGuide: "450000" }));
    } catch (err) {
      setError("AI valuation temporarily unavailable. Please enter price manually.");
    } finally {
      setLoadingAIEstimate(false);
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setCreating(true);
    try {
      const payload = {
        refCode: formState.refCode.trim(),
        status: formState.status,
        priceGuide: Number(formState.priceGuide) || 0,
        beds: Number(formState.beds) || 0,
        baths: Number(formState.baths) || 0,
        sqft: formState.sqft ? Number(formState.sqft) : undefined,
        tenure: formState.tenure ? formState.tenure.trim() : undefined,
        address: {
          line1: formState.line1.trim(),
          line2: formState.line2.trim() || undefined,
          city: formState.city.trim(),
          postcode: formState.postcode.trim()
        },
        description: formState.description.trim() || undefined,
        features: formState.features
          ? formState.features
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean)
          : []
      };
      const listing = await createListing(payload);
      onCreated?.(listing);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create listing");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4 py-10">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden />
      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-2xl rounded-3xl border border-white/10 bg-neutral-900/90 p-6 shadow-2xl backdrop-blur"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Create new listing</h2>
          <button type="button" className="text-sm text-white/60" onClick={onClose}>
            Cancel
          </button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="text-xs uppercase tracking-[0.3em] text-white/60">
            Ref code
            <input
              name="refCode"
              value={formState.refCode}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60">
            Status
            <select
              name="status"
              value={formState.status}
              onChange={handleChange}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none"
            >
              {CRM_STATUS_OPTIONS.slice(1).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60">
            Price guide
            <div className="relative mt-1">
              <input
                name="priceGuide"
                value={formState.priceGuide}
                onChange={handleChange}
                type="number"
                min="0"
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 pr-24 text-sm text-white focus:border-emerald-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleGetAIEstimate}
                disabled={loadingAIEstimate}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg border border-purple-400/40 bg-purple-500/20 px-2 py-1 text-xs text-purple-200 hover:bg-purple-500/30 disabled:opacity-50 transition-all flex items-center gap-1"
              >
                {loadingAIEstimate ? (
                  <>...</>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    AI
                  </>
                )}
              </button>
            </div>
            {aiEstimate && (
              <div className="mt-2 rounded-lg border border-purple-400/30 bg-purple-500/10 p-2 text-xs text-purple-100">
                <p className="font-medium">AI Estimate: £{aiEstimate.estimate.toLocaleString()}</p>
                <p className="text-purple-200/70">Range: £{aiEstimate.range.min.toLocaleString()} - £{aiEstimate.range.max.toLocaleString()}</p>
                <p className="text-purple-200/60 text-[10px] mt-1">{aiEstimate.note}</p>
              </div>
            )}
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60">
            Beds
            <input
              name="beds"
              value={formState.beds}
              onChange={handleChange}
              type="number"
              min="0"
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60">
            Baths
            <input
              name="baths"
              value={formState.baths}
              onChange={handleChange}
              type="number"
              min="0"
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60">
            Sqft
            <input
              name="sqft"
              value={formState.sqft}
              onChange={handleChange}
              type="number"
              min="0"
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60 md:col-span-2">
            Line 1
            <input
              name="line1"
              value={formState.line1}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60 md:col-span-2">
            Line 2
            <input
              name="line2"
              value={formState.line2}
              onChange={handleChange}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60">
            City
            <input
              name="city"
              value={formState.city}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60">
            Postcode
            <input
              name="postcode"
              value={formState.postcode}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60 md:col-span-2">
            Description
            <textarea
              name="description"
              value={formState.description}
              onChange={handleChange}
              rows={3}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.3em] text-white/60 md:col-span-2">
            Features (comma separated)
            <input
              name="features"
              value={formState.features}
              onChange={handleChange}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
            />
          </label>
        </div>
        {error && (
          <div className="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {error}
          </div>
        )}
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-white/15 px-4 py-2 text-sm text-white hover:bg-white/10 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creating}
            className="rounded-2xl border border-emerald-400/40 bg-emerald-500/80 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 transition disabled:opacity-70"
          >
            {creating ? "Creating…" : "Create listing"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function CrmViewingsPage() {
  const { viewings, preferences } = useCrmData();
  const viewingStyle = preferences?.viewingStyle || "hosted";
  const [selectedId, setSelectedId] = useState(null);
  const [focusId, setFocusId] = useState(null);

  useEffect(() => {
    const storedFocus = readSessionValue("crm:focusViewing");
    if (storedFocus) {
      setSelectedId(storedFocus);
      setFocusId(storedFocus);
      removeSessionValue("crm:focusViewing");
    } else if (viewings.length) {
      setSelectedId(viewings[0].id);
    }
    if (readSessionValue("crm:viewingsFocus")) {
      removeSessionValue("crm:viewingsFocus");
    }
  }, []);

  useEffect(() => {
    if (!viewings.length) {
      setSelectedId(null);
      return;
    }
    if (selectedId && viewings.some((viewing) => viewing.id === selectedId)) return;
    setSelectedId(viewings[0].id);
  }, [viewings, selectedId]);

  useEffect(() => {
    if (!focusId) return undefined;
    const timer = setTimeout(() => setFocusId(null), 4000);
    return () => clearTimeout(timer);
  }, [focusId]);

  const selectedViewing = viewings.find((viewing) => viewing.id === selectedId) || null;

  const calendarDays = useMemo(() => {
    const start = new Date();
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(start.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      return {
        date,
        label: date.toLocaleDateString("en-GB", { weekday: "short" }),
        display: date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
      };
    });
  }, []);

  const hours = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

  const slots = calendarDays.map((day) => {
    const dayKey = day.date.toDateString();
    const dailyViewings = viewings.filter(
      (viewing) => viewing.occursAt && viewing.occursAt.toDateString() === dayKey
    );
    return hours.map((hour) =>
      dailyViewings.filter((viewing) => viewing.occursAt && viewing.occursAt.getHours() === hour)
    );
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-white">Viewings calendar</h1>
          <p className="text-sm text-white/60">
            Weekly overview with quick links to confirm, reschedule, or capture feedback.
          </p>
          <p className="text-xs text-white/40 mt-1">
            Current hosting preference:{" "}
            {viewingStyle === "owner_led" ? "Owner-led slots" : "Hosted by HOME AI team"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() =>
              alert("Google Calendar sync coming soon — OAuth hook will be wired to Google Workspace.")
            }
            className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10 transition"
          >
            Connect Google Calendar
          </button>
          <button
            onClick={() => alert("ICS export placeholder — integrate with backend to download schedule.")}
            className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10 transition"
          >
            Export (.ics)
          </button>
        </div>
      </header>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-4">
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-2 text-sm">
            <thead>
              <tr>
                <th className="w-24 px-2 py-3 text-left text-xs uppercase tracking-[0.3em] text-white/40">Time</th>
                {calendarDays.map((day) => (
                  <th
                    key={day.date.toISOString()}
                    className="min-w-[150px] px-2 py-3 text-left text-xs uppercase tracking-[0.3em] text-white/40"
                  >
                    <div className="text-white text-base font-semibold">{day.label}</div>
                    <div className="text-xs text-white/60">{day.display}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hours.map((hour, rowIndex) => (
                <tr key={hour}>
                  <td className="px-2 py-3 text-xs text-white/40">{`${hour}:00`}</td>
                  {calendarDays.map((day, colIndex) => {
                    const cellViewings = slots[colIndex][rowIndex];
                    return (
                      <td key={day.date.toISOString() + hour} className="align-top">
                        {cellViewings.map((viewing) => {
                          const isFocused = viewing.id === focusId;
                          return (
                            <button
                              key={viewing.id}
                              type="button"
                              onClick={() => setSelectedId(viewing.id)}
                              className={
                                "mb-2 w-full rounded-2xl border px-3 py-2 text-left transition " +
                                (viewing.id === selectedId
                                  ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-100"
                                  : "border-white/10 bg-white/5 text-white hover:border-white/20")
                              }
                            >
                              <p className="text-xs uppercase tracking-[0.3em] text-white/50">
                                {formatShortTime(viewing.occursAt)}
                              </p>
                              <p className="text-sm font-medium text-white">
                                {viewing.listing?.title || "Listing"}
                              </p>
                              <p className="text-xs text-white/70">{viewing.buyerLabel || "Buyer"}</p>
                              {isFocused && (
                                <span className="mt-2 inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.35em] text-emerald-200">
                                  From search
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
        {!selectedViewing ? (
          <div className="text-sm text-white/60">Select a viewing to see the full brief.</div>
        ) : (
          <div className="space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Viewing brief</p>
              <h2 className="mt-2 text-2xl font-semibold text-white">
                {selectedViewing.listing?.title || "Viewing"}
              </h2>
              <p className="mt-1 text-sm text-white/70">
                {formatShortDateTime(selectedViewing.occursAt)} • {selectedViewing.listing?.area || "Area TBC"}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-white/80 space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-white/45">Buyer</p>
                <p className="text-white font-medium">{selectedViewing.buyerLabel || "Buyer"}</p>
                <p className="text-xs text-white/60">{selectedViewing.buyerEmail || "Email pending"}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-white/80 space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-white/45">Seller</p>
                <p className="text-white font-medium">
                  {selectedViewing.seller?.contactName || deriveDisplayName(selectedViewing.seller?.email)}
                </p>
                <p className="text-xs text-white/60">{selectedViewing.seller?.email || "Email pending"}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span
                  className={
                    "inline-flex items-center rounded-full border px-3 py-1 text-xs " +
                    badgeClassForStatus(selectedViewing.status)
                  }
                >
                  {selectedViewing.status}
                </span>
                <span className="text-[11px] uppercase tracking-[0.3em] text-white/30">
                  {selectedViewing.agent || "Agent TBC"}
                </span>
              </div>
              <p className="text-sm text-white/80">
                {selectedViewing.notes || "No internal notes captured yet."}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-white/80 space-y-1">
                <p className="text-xs uppercase tracking-[0.3em] text-white/45">Address</p>
                <p>{selectedViewing.listing?.address || selectedViewing.seller?.address || "—"}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-white/80 space-y-1">
                <p className="text-xs uppercase tracking-[0.3em] text-white/45">Next action</p>
                <p>
                  {selectedViewing.status?.toLowerCase().includes("await")
                    ? "Confirm availability with seller."
                    : "Collect feedback and share summary with seller."}
                </p>
              </div>
            </div>

            {selectedViewing.feedbackEntry ? (
              <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-sm text-emerald-50 space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-emerald-200">Buyer workspace</p>
                <p className="text-sm">
                  Sentiment: <span className="font-semibold">{selectedViewing.feedbackEntry.sentiment}</span>
                </p>
                {selectedViewing.feedbackEntry.offerAmount ? (
                  <p className="text-sm">
                    Submitted offer: {formatCurrency(selectedViewing.feedbackEntry.offerAmount)}
                  </p>
                ) : (
                  <p className="text-sm">No offer attached yet.</p>
                )}
                {selectedViewing.feedbackEntry.comments && (
                  <p className="text-xs text-emerald-100/80">“{selectedViewing.feedbackEntry.comments}”</p>
                )}
                {selectedViewing.feedbackEntry.documents?.length ? (
                  <div>
                    <p className="text-xs text-emerald-200 uppercase tracking-[0.3em]">Documents</p>
                    <ul className="text-xs text-emerald-100 list-disc pl-5">
                      {selectedViewing.feedbackEntry.documents.map((doc) => (
                        <li key={doc}>{doc}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-sm">
                    <p className="text-white/90 font-medium mb-1">Automated feedback request sent</p>
                    <p className="text-white/60 text-xs">
                      {selectedViewing.status === "COMPLETED" && selectedViewing.end ? (
                        <>System automatically sends feedback requests 1-24 hours after viewing completion. 
                        Next auto-reminder in 48 hours if no response.</>
                      ) : selectedViewing.start && new Date(selectedViewing.start) > new Date() ? (
                        <>Automated reminder will be sent 24 hours before viewing ({formatShortDateTime(new Date(new Date(selectedViewing.start).getTime() - 24*60*60*1000))}).</>
                      ) : (
                        <>Awaiting buyer feedback. System sends automated requests after viewing completion.</>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="w-full rounded-xl border border-white/20 bg-white/5 px-3 py-2 text-xs text-white/80 hover:bg-white/10 hover:border-white/30 transition-all"
                  onClick={() => {
                    // Manual override - would trigger immediate reminder
                    alert('Manual reminder feature: Would send immediate feedback request to buyer.');
                  }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send manual reminder now
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export function CrmOffersPage() {
  const data = useCrmData();
  const [apiOffers, setApiOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [offersError, setOffersError] = useState(null);
  
  // Fetch offers from API
  useEffect(() => {
    let active = true;
    setOffersLoading(true);
    getOffers()
      .then((response) => {
        if (!active) return;
        // Transform API offers to match frontend format
        const transformed = (response.items || []).map((offer) => ({
          id: offer.id,
          listingId: offer.listingId,
          listing: {
            id: offer.listing?.id,
            title: offer.listing?.address?.line1 || offer.listing?.refCode || "Listing",
            refCode: offer.listing?.refCode,
            price: offer.listing?.priceGuide,
            address: offer.listing?.address
          },
          buyerContactId: offer.buyerContactId,
          buyerLabel: offer.buyer?.name || offer.buyer?.emails?.[0] || "Buyer",
          amount: Number(offer.amount),
          amountDisplay: formatCurrency(Number(offer.amount)),
          status: offer.status,
          notes: offer.conditions || "",
          proofOfFundsUrl: offer.proofOfFundsUrl,
          updatedAt: offer.updatedAt ? new Date(offer.updatedAt) : null,
          createdAt: offer.createdAt ? new Date(offer.createdAt) : null,
          editable: true,
          source: "api"
        }));
        setApiOffers(transformed);
      })
      .catch((error) => {
        if (!active) return;
        setOffersError(error?.message || "Unable to load offers");
        console.error("Failed to load offers:", error);
      })
      .finally(() => {
        if (!active) return;
        setOffersLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Use API offers exclusively (no localStorage merge)
  const offers = useMemo(() => apiOffers, [apiOffers]);

  const [selectedId, setSelectedId] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [stageFilter, setStageFilter] = useState(() => readSessionValue("crm:offersFilter", "active"));
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const stored = readSessionValue("crm:focusOffer");
    if (stored) {
      setSelectedId(stored);
      setFocusId(stored);
      removeSessionValue("crm:focusOffer");
    }
    const storedStage = readSessionValue("crm:offersFilter");
    if (storedStage) {
      setStageFilter(storedStage);
      removeSessionValue("crm:offersFilter");
    }
  }, []);

  useEffect(() => {
    if (!focusId) return undefined;
    const timer = setTimeout(() => setFocusId(null), 4000);
    return () => clearTimeout(timer);
  }, [focusId]);

  const stageOptions = [
    { label: "Active pipeline", value: "active" },
    { label: "Awaiting proof", value: "await" },
    { label: "Counter", value: "counter" },
    { label: "Accepted", value: "accept" },
    { label: "Completed", value: "complete" },
    { label: "Withdrawn", value: "withdraw" },
     { label: "Agent logged", value: "agent" },
    { label: "All stages", value: "all" }
  ];

  const filteredOffers = useMemo(() => {
    return offers.filter((offer) => {
      const status = (offer.status || "").toLowerCase();
      const isArchived =
        status.includes("complete") || status.includes("withdraw") || status.includes("declin");
      if (stageFilter === "agent") {
        return offer.source === "agent";
      }
      if (!showArchived && isArchived) return stageFilter === "complete" || stageFilter === "withdraw" ? status.includes(stageFilter) : false;
      if (stageFilter === "active") {
        return !isArchived;
      }
      if (stageFilter === "all") return showArchived || !isArchived ? true : false;
      return status.includes(stageFilter);
    });
  }, [offers, showArchived, stageFilter]);

  useEffect(() => {
    if (!filteredOffers.length) {
      setSelectedId(null);
      return;
    }
    if (selectedId && filteredOffers.some((offer) => offer.id === selectedId)) return;
    setSelectedId(filteredOffers[0].id);
  }, [filteredOffers, selectedId]);

  const selectedOffer = offers.find((offer) => offer.id === selectedId) || null;

  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    if (!selectedOffer || !selectedOffer.editable) {
      setEditForm(null);
      return;
    }
    setEditForm({
      status: selectedOffer.status || "",
      stage: selectedOffer.stage || "",
      notes: selectedOffer.notes || "",
      outstanding: (selectedOffer.outstanding || []).join(", "),
      document: ""
    });
  }, [selectedOffer]);

  const handleOfferUpdate = async () => {
    if (!selectedOffer || !editForm) return;
    
    // If it's an API offer, update via API
    if (selectedOffer.source === "api") {
      try {
        const payload = {
          status: editForm.status,
          conditions: editForm.notes,
          ...(editForm.stage && { timestamps: { stage: editForm.stage } })
        };
        await updateOffer(selectedOffer.id, payload);
        // Refresh offers
        const response = await getOffers();
        const transformed = (response.items || []).map((offer) => ({
          id: offer.id,
          listingId: offer.listingId,
          listing: {
            id: offer.listing?.id,
            title: offer.listing?.address?.line1 || offer.listing?.refCode || "Listing",
            refCode: offer.listing?.refCode,
            price: offer.listing?.priceGuide,
            address: offer.listing?.address
          },
          buyerContactId: offer.buyerContactId,
          buyerLabel: offer.buyer?.name || offer.buyer?.emails?.[0] || "Buyer",
          amount: Number(offer.amount),
          amountDisplay: formatCurrency(Number(offer.amount)),
          status: offer.status,
          notes: offer.conditions || "",
          updatedAt: offer.updatedAt ? new Date(offer.updatedAt) : null,
          editable: true,
          source: "api"
        }));
        setApiOffers(transformed);
      } catch (error) {
        console.error("Failed to update offer:", error);
        alert("Failed to update offer: " + (error?.message || "Unknown error"));
      }
    }
  };

  const handleAgentOfferCreate = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const buyerEmail = String(form.get("buyerEmail") || "").trim();
    const amount = Number.parseInt(form.get("offerAmount"), 10);
    const listingId = String(form.get("listingId") || BASE_LISTING_ID).trim();
    const notes = String(form.get("notes") || "").trim();
    
    if (!buyerEmail || Number.isNaN(amount)) return;
    
    try {
      // Find or create buyer contact
      const contacts = await getContacts({ q: buyerEmail });
      let buyerContactId = contacts.items?.[0]?.id;
      
      if (!buyerContactId) {
        // Create contact if not found
        const newContact = await createContact({
          type: "BUYER",
          name: deriveDisplayName(buyerEmail),
          emails: [buyerEmail],
          phones: [],
          tags: []
        });
        buyerContactId = newContact.id;
      }
      
      // Create offer via API
      const payload = {
        listingId,
        buyerContactId,
        amount,
        status: "RECEIVED",
        conditions: notes
      };
      const created = await createOffer(payload);
      
      // Transform and add to state
      const transformed = {
        id: created.id,
        listingId: created.listingId,
        listing: {
          id: created.listing?.id,
          title: created.listing?.address?.line1 || created.listing?.refCode || "Listing",
          refCode: created.listing?.refCode,
          price: created.listing?.priceGuide,
          address: created.listing?.address
        },
        buyerContactId: created.buyerContactId,
        buyerLabel: created.buyer?.name || created.buyer?.emails?.[0] || "Buyer",
        amount: Number(created.amount),
        amountDisplay: formatCurrency(Number(created.amount)),
        status: created.status,
        notes: created.conditions || "",
        updatedAt: created.updatedAt ? new Date(created.updatedAt) : new Date(),
        editable: true,
        source: "api"
      };
      
      setApiOffers((prev) => [transformed, ...prev]);
      event.currentTarget.reset();
      setSelectedId(created.id);
      setFocusId(created.id);
    } catch (error) {
      console.error("Failed to create offer:", error);
      alert("Failed to create offer: " + (error?.message || "Unknown error"));
    }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold text-white">Offers</h1>
        <p className="text-sm text-white/60">
          Track negotiations, proof-of-funds, and seller updates in real time.
        </p>
        {offersError && (
          <div className="mt-2 rounded-lg border border-rose-400/40 bg-rose-500/10 px-4 py-2 text-sm text-rose-200">
            {offersError}
          </div>
        )}
        {offersLoading && (
          <div className="mt-2 text-sm text-white/60">Loading offers...</div>
        )}
      </header>
      <div className="grid gap-6 lg:grid-cols-[2fr,3fr] xl:grid-cols-[5fr,6fr]">
        <section className="rounded-3xl border border-white/10 bg-white/5">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-4 text-sm text-white/70">
            <span>
              {filteredOffers.length} offer{filteredOffers.length === 1 ? "" : "s"} in view
            </span>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {stageOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStageFilter(option.value)}
                  className={
                    "rounded-full border px-3 py-1 transition " +
                    (stageFilter === option.value
                      ? "border-sky-400/40 bg-sky-500/15 text-sky-100"
                      : "border-white/10 bg-white/5 text-white/60 hover:border-white/20")
                  }
                >
                  {option.label}
                </button>
              ))}
              <label className="ml-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
                <input
                  type="checkbox"
                  className="h-3 w-3 rounded border-white/20 bg-transparent"
                  checked={showArchived}
                  onChange={(event) => setShowArchived(event.target.checked)}
                />
                Archived
              </label>
            </div>
          </div>
          <div className="divide-y divide-white/10">
            {filteredOffers.length === 0 ? (
              <div className="px-5 py-12 text-sm text-white/60">
                No offers match your filters. Adjust the stage or archived toggle.
              </div>
            ) : (
              filteredOffers.map((offer) => {
                const isSelected = offer.id === selectedId;
                const isFocused = offer.id === focusId;
                return (
                  <button
                    key={offer.id}
                    type="button"
                    onClick={() => setSelectedId(offer.id)}
                    className={
                      "w-full px-5 py-4 text-left transition " +
                      (isSelected ? "bg-white/10" : "hover:bg-white/5")
                    }
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-white">
                          {offer.listing?.title || "Listing"}
                        </p>
                        <p className="text-xs text-white/60">
                          {offer.buyerLabel || "Buyer"} • {offer.amountDisplay}
                        </p>
                      </div>
                      <div className="text-right text-xs text-white/60">
                        <div className="font-semibold text-white">{formatRelative(offer.updatedAt)}</div>
                        <div>{offer.updatedAt ? formatShortDate(offer.updatedAt) : "—"}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      <span
                        className={
                          "inline-flex items-center rounded-full border px-3 py-1 " +
                          badgeClassForStatus(offer.status)
                        }
                      >
                        {offer.status}
                      </span>
                      {isFocused && (
                        <span className="inline-flex items-center rounded-full border border-sky-400/40 bg-sky-500/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.35em] text-sky-200">
                          Jumped from search
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>
        <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
          {!selectedOffer ? (
            <div className="text-sm text-white/60">
              Select an offer to see the negotiation context and outstanding tasks.
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">Offer detail</p>
                <h2 className="mt-2 text-2xl font-semibold text-white">
                  {selectedOffer.listing?.title || "Offer"}
                </h2>
                <p className="mt-1 text-sm text-white/70">
                  {selectedOffer.buyerLabel || "Buyer"} • {selectedOffer.amountDisplay}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-white/80 space-y-1">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/45">Status</p>
                  <p className="text-white font-medium">{selectedOffer.status}</p>
                  <p className="text-xs text-white/60">
                    Updated {selectedOffer.updatedAt ? formatShortDateTime(selectedOffer.updatedAt) : "—"}
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-white/80 space-y-1">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/45">Seller</p>
                  <p className="text-white font-medium">
                    {selectedOffer.seller?.contactName || deriveDisplayName(selectedOffer.seller?.email)}
                  </p>
                  <p className="text-xs text-white/60">{selectedOffer.seller?.email || "—"}</p>
                </div>
              </div>

              {/* Automation Status Badge */}
              <div className="rounded-2xl border border-sky-400/30 bg-sky-500/10 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-sky-100 font-medium">Automated notifications active</p>
                    <p className="text-xs text-sky-200/70 mt-1">
                      {selectedOffer.status?.toLowerCase().includes("received") || selectedOffer.status?.toLowerCase().includes("under") ? (
                        <>Seller was automatically notified of this offer when submitted. Status updates will be sent automatically to buyer.</>
                      ) : selectedOffer.status?.toLowerCase().includes("accept") ? (
                        <>Buyer was automatically notified of acceptance. Next steps notifications will follow automatically.</>
                      ) : selectedOffer.status?.toLowerCase().includes("counter") ? (
                        <>Buyer was automatically notified of counter-offer. Awaiting their response.</>
                      ) : selectedOffer.status?.toLowerCase().includes("reject") ? (
                        <>Buyer was automatically notified of rejection. They may submit a revised offer.</>
                      ) : (
                        <>All parties receive automatic email notifications when offer status changes.</>
                      )}
                    </p>
                    {selectedOffer.updatedAt && (
                      <p className="text-xs text-sky-200/50 mt-2">
                        Last notification sent: {formatShortDateTime(selectedOffer.updatedAt)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3">
                <p className="text-xs uppercase tracking-[0.3em] text-white/45">Notes & actions</p>
                <p className="text-sm text-white/80">
                  {selectedOffer.notes || "No notes yet. Log calls, proof-of-funds updates, or solicitor responses here."}
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-white/70">
                  <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                    Proof: {selectedOffer.status.toLowerCase().includes("proof") ? "Awaiting" : "Received"}
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                    Terms: {selectedOffer.status.toLowerCase().includes("counter") ? "Counter issued" : "Standard"}
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-white/80 space-y-1">
                <p className="text-xs uppercase tracking-[0.3em] text-white/45">Linked listing</p>
                <p>{selectedOffer.listing?.address || selectedOffer.listing?.area || "Address pending"}</p>
                <p className="text-xs text-white/60">
                  Asking {selectedOffer.listing ? formatCurrency(selectedOffer.listing.price) : "—"}
                </p>
              </div>
              {selectedOffer.editable && editForm && (
                <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4 space-y-3 text-sm text-emerald-50">
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-200">Buyer workspace offer</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="text-xs text-emerald-100">
                      Status
                      <input
                        className="mt-1 w-full rounded-xl border border-emerald-400/40 bg-black/30 px-3 py-2 text-sm text-white"
                        value={editForm.status}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                      />
                    </label>
                    <label className="text-xs text-emerald-100">
                      Stage
                      <input
                        className="mt-1 w-full rounded-xl border border-emerald-400/40 bg-black/30 px-3 py-2 text-sm text-white"
                        value={editForm.stage}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, stage: e.target.value }))}
                      />
                    </label>
                  </div>
                  <label className="text-xs text-emerald-100">
                    Notes
                    <textarea
                      className="mt-1 w-full rounded-xl border border-emerald-400/40 bg-black/30 px-3 py-2 text-sm text-white"
                      rows={3}
                      value={editForm.notes}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
                    />
                  </label>
                  <label className="text-xs text-emerald-100">
                    Outstanding items (comma separated)
                    <input
                      className="mt-1 w-full rounded-xl border border-emerald-400/40 bg-black/30 px-3 py-2 text-sm text-white"
                      value={editForm.outstanding}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, outstanding: e.target.value }))}
                    />
                  </label>
                  <label className="text-xs text-emerald-100">
                    Add document reference
                    <input
                      className="mt-1 w-full rounded-xl border border-emerald-400/40 bg-black/30 px-3 py-2 text-sm text-white"
                      value={editForm.document}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, document: e.target.value }))}
                      placeholder="Proof of funds.pdf"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleOfferUpdate}
                    className="w-full rounded-full bg-white text-black py-3 text-sm font-semibold uppercase tracking-[0.3em] hover:bg-white/90"
                  >
                    Update offer record
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
      <section className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white">Log manual offer</h2>
            <p className="text-sm text-white/70">
              Capture phone-in offers or note buyer interest directly from the CRM.
            </p>
          </div>
        </div>
        <form className="grid gap-3 md:grid-cols-3" onSubmit={handleAgentOfferCreate}>
          <input
            name="buyerEmail"
            type="email"
            placeholder="buyer@example.com"
            className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none md:col-span-1"
            required
          />
          <input
            name="offerAmount"
            type="number"
            placeholder="Offer amount"
            className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none md:col-span-1"
            required
          />
          <input
            name="notes"
            type="text"
            placeholder="Notes / conditions"
            className="rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none md:col-span-1"
          />
          <div className="md:col-span-3 flex justify-end">
            <button
              type="submit"
              className="rounded-xl bg-white text-black px-4 py-2 text-sm font-medium hover:bg-white/90"
            >
              Add offer
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export function CrmSalesPage() {
  return (
    <CrmPlaceholder
      title="Sales Progression"
      description="Stage-based pipeline with SLA alerts, milestones, and conveyancer collaboration."
    />
  );
}

export function CrmLettersPage() {
  return (
    <CrmPlaceholder
      title="Letters & Emails"
      description="MJML templates, Gmail sending, PDF generation, and entity-linked history."
    />
  );
}

export function CrmSettingsPage() {
  const { session } = useOutletContext();
  const [settings, setSettings] = React.useState({
    displayName: session?.name || "",
    email: session?.email || "",
    phone: "",
    title: "Real Estate Agent",
    timezone: "America/New_York",
    notifications: {
      emailOnNewLead: true,
      emailOnNewOffer: true,
      emailOnNewViewing: true,
      inAppNotifications: true
    },
    privacy: {
      showOnlineStatus: true,
      allowMessages: true,
      dataTracking: false
    },
    theme: "dark"
  });
  const [saved, setSaved] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("profile");

  const handleSettingChange = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split(".");
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
    setSaved(false);
  };

  const handleSaveSettings = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleLogout = () => {
    clearSession();
    window.location.href = "/";
  };

  const tabs = [
    { id: "profile", label: "Profile", icon: "👤" },
    { id: "security", label: "Security", icon: "🔒" },
    { id: "notifications", label: "Notifications", icon: "🔔" },
    { id: "preferences", label: "Preferences", icon: "⚙️" },
    { id: "privacy", label: "Privacy", icon: "🛡️" },
    { id: "billing", label: "Billing", icon: "💳" }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl p-8">
        <h1 className="text-4xl font-light text-white">Account Settings</h1>
        <p className="text-sm text-white/60 mt-2">Manage your profile, security, and preferences.</p>
      </section>

      {/* Tabs */}
      <div className="rounded-[32px] border border-white/10 bg-black/60 backdrop-blur-2xl">
        <div className="flex flex-wrap gap-1 border-b border-white/5 px-8 py-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={[
                "px-5 py-2 rounded-2xl text-sm font-medium transition-all duration-300",
                activeTab === tab.id
                  ? "bg-white/10 text-white border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              ].join(" ")}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-8">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <label className="block text-xs uppercase tracking-[0.35em] text-white/40 mb-2">Display Name</label>
                <input
                  type="text"
                  value={settings.displayName}
                  onChange={(e) => handleSettingChange("displayName", e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-white placeholder-white/30 focus:border-white/30 focus:outline-none focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.35em] text-white/40 mb-2">Email Address</label>
                <input
                  type="email"
                  value={settings.email}
                  disabled
                  className="w-full rounded-2xl border border-white/10 bg-black/50 px-4 py-3 text-white/60 cursor-not-allowed"
                />
                <p className="text-xs text-white/40 mt-2">Email cannot be changed. Contact support to update.</p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.35em] text-white/40 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => handleSettingChange("phone", e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-white placeholder-white/30 focus:border-white/30 focus:outline-none focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.35em] text-white/40 mb-2">Professional Title</label>
                <input
                  type="text"
                  value={settings.title}
                  onChange={(e) => handleSettingChange("title", e.target.value)}
                  placeholder="Real Estate Agent"
                  className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-white placeholder-white/30 focus:border-white/30 focus:outline-none focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition"
                />
              </div>

              <button
                onClick={handleSaveSettings}
                className="rounded-2xl bg-white text-black px-6 py-3 text-sm font-medium hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300"
              >
                {saved ? "✓ Saved" : "Save Changes"}
              </button>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === "security" && (
            <div className="space-y-6 max-w-2xl">
              <div className="rounded-2xl border border-white/10 bg-black/40 p-6">
                <h3 className="text-sm font-medium text-white mb-2">Change Password</h3>
                <p className="text-xs text-white/60 mb-4">Update your password to keep your account secure.</p>
                <button className="rounded-2xl border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/5 hover:border-white/40 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300">
                  Change Password
                </button>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/40 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-white mb-1">Two-Factor Authentication</h3>
                    <p className="text-xs text-white/60">Add an extra layer of security to your account.</p>
                  </div>
                  <button className="rounded-2xl border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/5 hover:border-white/40 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300">
                    Enable 2FA
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/40 p-6">
                <h3 className="text-sm font-medium text-white mb-2">Active Sessions</h3>
                <p className="text-xs text-white/60 mb-4">Manage your logged-in devices.</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <div>
                      <p className="text-sm text-white">This Device</p>
                      <p className="text-xs text-white/60">Current session</p>
                    </div>
                    <span className="text-xs text-emerald-400">Active</span>
                  </div>
                </div>
                <button className="mt-4 rounded-2xl border border-rose-500/30 px-4 py-2 text-sm text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/50 hover:shadow-[0_0_15px_rgba(255,0,0,0.1)] transition-all duration-300">
                  Logout All Devices
                </button>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h3 className="text-sm font-medium text-white mb-4">Email Notifications</h3>
                <div className="space-y-3">
                  {[
                    { key: "emailOnNewLead", label: "New leads", desc: "Get notified when a new lead is added" },
                    { key: "emailOnNewOffer", label: "New offers", desc: "Get notified when an offer is submitted" },
                    { key: "emailOnNewViewing", label: "New viewings", desc: "Get notified when a viewing is scheduled" }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/40 p-4">
                      <div>
                        <p className="text-sm font-medium text-white">{item.label}</p>
                        <p className="text-xs text-white/60">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => handleSettingChange(`notifications.${item.key}`, !settings.notifications[item.key])}
                        className={`relative inline-flex h-6 w-11 rounded-full transition-all ${settings.notifications[item.key] ? "bg-emerald-500" : "bg-white/10"}`}
                      >
                        <span className={`inline-block h-5 w-5 rounded-full bg-white transition-all ${settings.notifications[item.key] ? "translate-x-6" : "translate-x-0.5"}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                className="rounded-2xl bg-white text-black px-6 py-3 text-sm font-medium hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300"
              >
                {saved ? "✓ Saved" : "Save Changes"}
              </button>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === "preferences" && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <label className="block text-xs uppercase tracking-[0.35em] text-white/40 mb-2">Timezone</label>
                <select
                  value={settings.timezone}
                  onChange={(e) => handleSettingChange("timezone", e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-white/30 focus:outline-none focus:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition"
                >
                  <option>America/New_York</option>
                  <option>America/Chicago</option>
                  <option>America/Denver</option>
                  <option>America/Los_Angeles</option>
                  <option>Europe/London</option>
                  <option>Europe/Paris</option>
                  <option>Asia/Tokyo</option>
                  <option>Australia/Sydney</option>
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.35em] text-white/40 mb-2">Theme</label>
                <div className="flex gap-3">
                  {["dark", "light"].map(theme => (
                    <button
                      key={theme}
                      onClick={() => handleSettingChange("theme", theme)}
                      className={`flex-1 rounded-2xl border px-4 py-3 text-sm font-medium transition-all ${
                        settings.theme === theme
                          ? "border-white/30 bg-white/10 text-white shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                          : "border-white/10 text-white/60 hover:text-white"
                      }`}
                    >
                      {theme === "dark" ? "🌙 Dark" : "☀️ Light"}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                className="rounded-2xl bg-white text-black px-6 py-3 text-sm font-medium hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300"
              >
                {saved ? "✓ Saved" : "Save Changes"}
              </button>
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === "privacy" && (
            <div className="space-y-6 max-w-2xl">
              <div className="space-y-3">
                {[
                  { key: "showOnlineStatus", label: "Show online status", desc: "Let others see when you're online" },
                  { key: "allowMessages", label: "Allow messages", desc: "Allow team members to message you" },
                  { key: "dataTracking", label: "Analytics & data tracking", desc: "Help us improve by sharing usage data" }
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/40 p-4">
                    <div>
                      <p className="text-sm font-medium text-white">{item.label}</p>
                      <p className="text-xs text-white/60">{item.desc}</p>
                    </div>
                    <button
                      onClick={() => handleSettingChange(`privacy.${item.key}`, !settings.privacy[item.key])}
                      className={`relative inline-flex h-6 w-11 rounded-full transition-all ${settings.privacy[item.key] ? "bg-emerald-500" : "bg-white/10"}`}
                    >
                      <span className={`inline-block h-5 w-5 rounded-full bg-white transition-all ${settings.privacy[item.key] ? "translate-x-6" : "translate-x-0.5"}`} />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={handleSaveSettings}
                className="rounded-2xl bg-white text-black px-6 py-3 text-sm font-medium hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300"
              >
                {saved ? "✓ Saved" : "Save Changes"}
              </button>
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === "billing" && (
            <div className="space-y-6 max-w-2xl">
              <div className="rounded-2xl border border-white/10 bg-black/40 p-6">
                <h3 className="text-sm font-medium text-white mb-2">Subscription Status</h3>
                <p className="text-2xl font-light text-white my-2">Professional Plan</p>
                <p className="text-xs text-white/60 mb-4">Next billing date: April 1, 2026</p>
                <button className="rounded-2xl border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/5 hover:border-white/40 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300">
                  Manage Subscription
                </button>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/40 p-6">
                <h3 className="text-sm font-medium text-white mb-4">Payment Methods</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-sm text-white">💳 Visa ending in 4242</p>
                      <p className="text-xs text-white/60">Expires 12/2027</p>
                    </div>
                    <button className="text-xs text-white/60 hover:text-white transition">Edit</button>
                  </div>
                </div>
                <button className="mt-4 rounded-2xl border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/5 hover:border-white/40 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300">
                  + Add Payment Method
                </button>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/40 p-6">
                <h3 className="text-sm font-medium text-white mb-2">Billing History</h3>
                <p className="text-xs text-white/60 mb-4">Download invoices and payment receipts.</p>
                <button className="rounded-2xl border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/5 hover:border-white/40 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300">
                  View Invoices
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Account Danger Zone */}
      <section className="rounded-[32px] border border-rose-500/20 bg-rose-500/5 backdrop-blur-2xl p-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-light text-rose-400">Danger Zone</h3>
            <p className="text-sm text-rose-300/70 mt-1">Account deletion and logout actions.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleLogout}
              className="rounded-2xl border border-white/20 px-6 py-3 text-sm font-medium text-white hover:bg-white/5 hover:border-white/40 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300"
            >
              Logout
            </button>
            <button className="rounded-2xl border border-rose-500/50 px-6 py-3 text-sm font-medium text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/70 hover:shadow-[0_0_15px_rgba(255,0,0,0.1)] transition-all duration-300">
              Delete Account
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
